// ============================================================================
// TRENCH CRUSADE - VANILLA FACTIONS DATA
// ============================================================================
//
// This file contains all official Trench Crusade faction data organized by:
// - Faction (top level)
// - Variants/Subfactions (nested within each faction)
//
// HOW TO ADD A NEW FACTION:
// 1. Add a new entry to the FACTIONS object below
// 2. Include at least a "Standard" variant
// 3. Add models and items arrays for each variant
//
// HOW TO ADD A SUBFACTION/VARIANT:
// 1. Add a new key to a faction's variants object
// 2. Include models and items arrays
//
// ============================================================================

import { PremadeModelTemplate, PremadeItemTemplate } from "../types";

// ============================================================================
// IRON SULTANATE - ITEMS
// ============================================================================

export const IRON_SULTANATE_STANDARD_ITEMS: any[] = [
  // ============================================================================
  // RANGED WEAPONS
  // ============================================================================
  {
    name: "Alaybozan",
    category: "ranged",
    keywords: ["2-HANDED", "SHRAPNEL", "Shield Combo"],
    range: "12\"",
    cost: 9,
    costCurrency: "ducats",
    restrictions: ["Sultanate Sappers only"],
    notes: ""
  },
  {
    name: "Flame Cannon",
    category: "ranged",
    keywords: ["2-HANDED", "FIRE", "FLAMETHROWER", "IGNORE ARMOUR", "HEAVY"],
    range: "12\"",
    cost: 60,
    costCurrency: "ducats",
    limit: 1,
    restrictions: ["Brazen Bull only"],
    notes: "Greek Fire: Measure 1mm wide line up to 12\". Injury Roll for every model touched (friend or foe except attacker)."
  },
  {
    name: "Flamethrower",
    category: "ranged",
    keywords: ["2-HANDED", "-1 INJURY DICE", "FIRE", "FLAMETHROWER", "IGNORE ARMOUR"],
    range: "8\"",
    cost: 30,
    costCurrency: "ducats",
    limit: 3,
    notes: ""
  },
  {
    name: "Jezzail",
    category: "ranged",
    keywords: ["2-HANDED"],
    range: "18\"",
    cost: 7,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Machine Gun",
    category: "ranged",
    keywords: ["2-HANDED", "AUTOMATIC 3", "HEAVY", "RELOAD"],
    range: "36\"",
    cost: 50,
    costCurrency: "ducats",
    limit: 1,
    notes: ""
  },
  {
    name: "M.U.R.A.D. Bombard",
    category: "ranged",
    keywords: ["2-HANDED", "BLAST 3\"", "HEAVY", "SHRAPNEL"],
    range: "36\"",
    cost: 50,
    costCurrency: "ducats",
    limit: 1,
    restrictions: ["Brazen Bull only"],
    notes: "Echoing Blast: Target hit: roll 3D6 + all dice (pick highest/lowest for modifiers). Other models in blast: Down only."
  },
  {
    name: "Musket",
    category: "ranged",
    keywords: ["2-HANDED", "Bayonet Lug"],
    range: "18\"",
    cost: 5,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Pistol",
    category: "ranged",
    keywords: ["1-HANDED", "PISTOL"],
    range: "12\"/Melee",
    cost: 6,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Shotgun",
    category: "ranged",
    keywords: ["2-HANDED", "Bayonet Lug", "Shield Combo"],
    range: "12\"",
    cost: 10,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Siege Jezzail",
    category: "ranged",
    keywords: ["2-HANDED", "+1 INJURY DICE", "HEAVY"],
    range: "30\"",
    cost: 30,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Sniper Rifle",
    category: "ranged",
    keywords: ["2-HANDED", "+1 DICE", "CRITICAL", "RISKY"],
    range: "48\"",
    cost: 35,
    costCurrency: "ducats",
    limit: 2,
    restrictions: ["ELITE & Janissaries only"],
    notes: "Bull's Eye: Critical Success Injury Roll gains IGNORE ARMOUR."
  },

  // ============================================================================
  // MELEE WEAPONS
  // ============================================================================
  {
    name: "Assassin's Dagger",
    category: "melee",
    keywords: ["1-HANDED", "+1 INJURY MODIFIER", "CRITICAL"],
    range: "Melee",
    cost: 15,
    costCurrency: "ducats",
    restrictions: ["Assassin only"],
    notes: "Slashing Attack: On BLOOD MARKER, can Dash Retreat. Enemies can't Opportunity Attack on Retreat."
  },
  {
    name: "Bayonet",
    category: "melee",
    keywords: ["2-HANDED", "CUMBERSOME", "Shield Combo"],
    range: "Melee",
    cost: 2,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Great Hammer/Maul",
    category: "melee",
    keywords: ["2-HANDED", "+1 INJURY MODIFIER", "HEAVY"],
    range: "Melee",
    cost: 10,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Great Sword/Axe",
    category: "melee",
    keywords: ["2-HANDED", "+1 INJURY DICE", "CRITICAL", "HEAVY"],
    range: "Melee",
    cost: 12,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Halberd-Gun",
    category: "melee",
    keywords: ["2-HANDED", "ASSAULT", "BLOCK", "CUMBERSOME"],
    range: "Melee/24\"",
    cost: 20,
    costCurrency: "ducats",
    restrictions: ["ELITE only"],
    notes: ""
  },
  {
    name: "Polearm",
    category: "melee",
    keywords: ["2-HANDED", "BLOCK", "CUMBERSOME", "Shield Combo"],
    range: "Melee",
    cost: 7,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Sword/Axe",
    category: "melee",
    keywords: ["1-HANDED", "CRITICAL"],
    range: "Melee",
    cost: 4,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Titan Zulfiqar",
    category: "melee",
    keywords: ["1-HANDED", "+2 INJURY MODIFIER", "CRITICAL", "HEAVY"],
    range: "Melee",
    cost: 30,
    costCurrency: "ducats",
    restrictions: ["Brazen Bull only"],
    notes: ""
  },
  {
    name: "Trench Club",
    category: "melee",
    keywords: ["1-HANDED"],
    range: "Melee",
    cost: 3,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Trench Knife",
    category: "melee",
    keywords: ["1-HANDED", "-1 DICE"],
    range: "Melee",
    cost: 1,
    costCurrency: "ducats",
    notes: ""
  },

  // ============================================================================
  // GRENADES
  // ============================================================================
  {
    name: "Frag Grenades",
    category: "grenade",
    keywords: ["ASSAULT", "BLAST 2\"", "IGNORE COVER", "IGNORE LONG RANGE", "SHRAPNEL"],
    range: "8\"",
    cost: 7,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Incendiary Grenades",
    category: "grenade",
    keywords: ["ASSAULT", "FIRE", "IGNORE COVER", "IGNORE LONG RANGE"],
    range: "8\"",
    cost: 15,
    costCurrency: "ducats",
    restrictions: ["Alchemist Only"],
    notes: "Liquid Fire: Critical Success Injury Roll gains IGNORE ARMOUR."
  },

  // ============================================================================
  // SHIELDS
  // ============================================================================
  {
    name: "Trench Shield",
    category: "shield",
    keywords: ["Shield Combo"],
    armourModifier: "-1 INJURY MODIFIER",
    cost: 10,
    costCurrency: "ducats",
    notes: ""
  },

  // ============================================================================
  // ARMOUR
  // ============================================================================
  {
    name: "Alchemist Armour",
    category: "armour",
    keywords: ["NEGATE FIRE", "NEGATE GAS"],
    armourModifier: "-2 INJURY MODIFIER",
    cost: 50,
    costCurrency: "ducats",
    limit: 1,
    restrictions: ["ELITE only"],
    notes: "Protection From Harm: -1 INJURY DICE vs FIRE/GAS (even IGNORE ARMOUR)."
  },
  {
    name: "Reinforced Armour",
    category: "armour",
    keywords: [],
    armourModifier: "-2 INJURY MODIFIER",
    cost: 40,
    costCurrency: "ducats",
    restrictions: ["ELITE & Janissaries only"],
    notes: ""
  },
  {
    name: "Standard Armour",
    category: "armour",
    keywords: [],
    armourModifier: "-1 INJURY MODIFIER",
    cost: 15,
    costCurrency: "ducats",
    notes: ""
  },

  // ============================================================================
  // EQUIPMENT
  // ============================================================================
  {
    name: "Alchemical Ammunition",
    category: "equipment",
    keywords: [],
    cost: 3,
    costCurrency: "ducats",
    restrictions: ["Models with Jezzail, Siege Jezzail, Alaybozan or Halberd-Gun only"],
    notes: "Guiding Path: +1 DICE to Ranged Attacks with those weapons."
  },
  {
    name: "Binoculars",
    category: "equipment",
    keywords: [],
    cost: 10,
    costCurrency: "ducats",
    restrictions: ["ELITE only"],
    notes: "INFILTRATOR cannot deploy within 16\" unless in own zone."
  },
  {
    name: "Cloak of Alamut",
    category: "equipment",
    keywords: [],
    cost: 25,
    costCurrency: "ducats",
    limit: 1,
    restrictions: ["ELITE only"],
    notes: "Blend In: Cover penalty -2 DICE instead of -1."
  },
  {
    name: "Combat Helmet",
    category: "equipment",
    keywords: ["NEGATE SHRAPNEL"],
    cost: 5,
    costCurrency: "ducats",
    notes: "Headgear"
  },
  {
    name: "Gas Mask",
    category: "equipment",
    keywords: ["NEGATE GAS"],
    cost: 5,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Holy Relic",
    category: "equipment",
    keywords: ["BLESSED 1"],
    cost: 2,
    costCurrency: "glory",
    restrictions: ["ELITE only"],
    notes: ""
  },
  {
    name: "Marid Shovel",
    category: "equipment",
    keywords: ["HEAVY"],
    cost: 15,
    costCurrency: "ducats",
    restrictions: ["Brazen Bull only"],
    notes: "COVER on Open terrain until move. Can use as Trench Club."
  },
  {
    name: "Medi-kit",
    category: "equipment",
    keywords: [],
    cost: 5,
    costCurrency: "ducats",
    notes: "Treat ACTION: Risky Success to remove BLOOD MARKER or stand up Down model within 1\"."
  },
  {
    name: "Mountaineer Kit",
    category: "equipment",
    keywords: [],
    cost: 3,
    costCurrency: "ducats",
    limit: 2,
    notes: "+1 DICE to climb sheer surfaces."
  },
  {
    name: "Musical Instrument",
    category: "equipment",
    keywords: ["HELD"],
    cost: 15,
    costCurrency: "ducats",
    limit: 1,
    notes: "+1 DICE to Dash for friendly within 4\"."
  },
  {
    name: "Shovel",
    category: "equipment",
    keywords: [],
    cost: 5,
    costCurrency: "ducats",
    notes: "At start of game, if on open terrain, counts as being in COVER until moved. Can be used as Trench Club if both hands free."
  },
  {
    name: "Takwin Anqā Bird",
    category: "equipment",
    keywords: [],
    cost: 2,
    costCurrency: "glory",
    limit: 1,
    restrictions: ["ELITE only"],
    notes: "Cause Confusion: Melee Success Rolls vs model become Risky. Enemy within 1\" must Risky Success to Retreat."
  },
  {
    name: "Troop Flag",
    category: "equipment",
    keywords: ["LEADER", "HELD"],
    cost: 1,
    costCurrency: "glory",
    limit: 1,
    notes: ""
  },
  {
    name: "Wind Amulet",
    category: "equipment",
    keywords: [],
    cost: 10,
    costCurrency: "ducats",
    limit: 2,
    notes: "Gusts of Wind: Once per game, +3\" Movement for activation."
  }
];

export const IRON_SULTANATE_ALAMUT_ITEMS: any[] = [
  ...IRON_SULTANATE_STANDARD_ITEMS,

  // ============================================================================
  // FIDA'I OF ALAMUT SPECIFIC ITEMS
  // ============================================================================
  {
    name: "Bow of Alamut",
    category: "ranged",
    keywords: ["2-HANDED", "CRITICAL", "IGNORE ARMOUR"],
    range: "40\"",
    cost: 25,
    costCurrency: "ducats",
    limit: 1,
    notes: "Temporal Slipstream: If attack causes BLOOD MARKERS, redeploy attacker within 1\" of target."
  },
  {
    name: "Golden Khanjar",
    category: "melee",
    keywords: ["1-HANDED", "+1 INJURY MODIFIER"],
    range: "Melee",
    cost: 20,
    costCurrency: "ducats",
    limit: 2,
    notes: "Attack Twice: Make 2 Melee Attacks with this Weapon, one after the other."
  },
  {
    name: "Hashashin Leaf",
    category: "equipment",
    keywords: ["CONSUMABLE"],
    cost: 12,
    costCurrency: "ducats",
    limit: 4,
    notes: "Enhanced Strength: On deploy, consume to gain STRONG Keyword for rest of game."
  }
];

// ============================================================================
// IRON SULTANATE - FIDA'I OF ALAMUT VARIANT
// ============================================================================

export const IRON_SULTANATE_ALAMUT_MODELS: PremadeModelTemplate[] = [
  // ============================================================================
  // ELITE MODELS
  // ============================================================================
  {
    name: "Master Assassin",
    class: "Master Assassin",
    type: "elite",
    cost: 95,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 2,
      ranged: 1,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["SULTANATE", "ELITE", "INFILTRATOR", "TOUGH"],
    abilities: [
      "Temporal Assassin: After Charge Bonus, can split through time to Charge two enemies and attack both once each.",
      "Time Slip: On failed enemy attack, redeploy within 6\" (>1\" from enemies)."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "master_assassin_hallucinogen",
        name: "Hallucinogen Disguise",
        cost: 20,
        effects: {
          addAbilities: [
            {
              name: "Hallucinogen Disguise",
              description: "If deployed using INFILTRATOR, must be ≥8\" from enemies but ignore all other INFILTRATOR restrictions."
            }
          ]
        }
      },
      {
        id: "master_assassin_mirage",
        name: "Mirage of Time",
        cost: 15,
        effects: {
          addAbilities: [
            {
              name: "Mirage of Time",
              description: "Add -1 DICE to Success Roll for attacks targeting this model."
            }
          ]
        }
      },
      {
        id: "master_assassin_secret_paths",
        name: "Secret Paths",
        cost: 10,
        effects: {
          addAbilities: [
            {
              name: "Secret Paths",
              description: "Not deployed at start. From Turn 2+, can Activate by deploying on any board edge >8\" from enemies."
            }
          ]
        }
      },
      {
        id: "master_assassin_thunderbolt",
        name: "Thunderbolt of Alamut",
        cost: 20,
        effects: {
          modifyStats: { movement: 2 }
        }
      }
    ]
  },
  {
    name: "Sultanate Assassin",
    class: "Sultanate Assassin",
    type: "elite",
    cost: 85,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 2,
      ranged: 1,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["SULTANATE", "ELITE", "INFILTRATOR"],
    abilities: [
      "Temporal Assassin: After Charge Bonus, can split through time to Charge two enemies and attack both once each.",
      "Time Slip: On failed enemy attack, redeploy within 6\" (>1\" from enemies)."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "assassin_hallucinogen",
        name: "Hallucinogen Disguise",
        cost: 20,
        effects: {
          addAbilities: [
            {
              name: "Hallucinogen Disguise",
              description: "If deployed using INFILTRATOR, must be ≥8\" from enemies but ignore all other INFILTRATOR restrictions."
            }
          ]
        }
      },
      {
        id: "assassin_mirage",
        name: "Mirage of Time",
        cost: 15,
        effects: {
          addAbilities: [
            {
              name: "Mirage of Time",
              description: "Add -1 DICE to Success Roll for attacks targeting this model."
            }
          ]
        }
      },
      {
        id: "assassin_secret_paths",
        name: "Secret Paths",
        cost: 10,
        effects: {
          addAbilities: [
            {
              name: "Secret Paths",
              description: "Not deployed at start. From Turn 2+, can Activate by deploying on any board edge >8\" from enemies."
            }
          ]
        }
      },
      {
        id: "assassin_thunderbolt",
        name: "Thunderbolt of Alamut",
        cost: 20,
        effects: {
          modifyStats: { movement: 2 }
        }
      }
    ]
  },

  // ============================================================================
  // TROOPER MODELS
  // ============================================================================
  {
    name: "Azeb",
    class: "Azeb",
    type: "trooper",
    cost: 25,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: -1,
      ranged: 0,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["SULTANATE"],
    abilities: [],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "azeb_acolyte_infiltrator",
        name: "Assassin Acolyte",
        cost: 10,
        limit: 3,
        effects: {
          addKeywords: ["INFILTRATOR"]
        }
      }
    ]
  },
  {
    name: "Dervish",
    class: "Dervish",
    type: "trooper",
    cost: 55,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 1,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["SULTANATE", "STRONG", "IGNORE OFF-HAND WEAPON"],
    abilities: [
      "Whirling Dervish: Add -1 DICE to Success Rolls for Ranged Attacks targeting this model.",
      "Counter-Charge: If first ACTION is Charge, add +1 DICE to Melee Attacks for rest of Activation."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: []
  },
  {
    name: "Sultanate Sapper",
    class: "Sultanate Sapper",
    type: "trooper",
    cost: 50,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 0,
      ranged: 1,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["SULTANATE"],
    abilities: [
      "Set Mine ACTION: Success +2 DICE → terrain (up to 8\"x8\") becomes Mined.",
      "Defuse Mine: Risky Success to defuse mine when moving into contact.",
      "Detonating Mines: Enemy moving into Mined terrain detonates (SHRAPNEL Injury Roll).",
      "Fortify ACTION: Risky Success → COVER until move.",
      "Forward Positions: Deploy up to 6\" forward in contact with terrain ≥½\" tall."
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "shovel-tool",
        name: "Shovel",
        category: "equipment",
        keywords: [],
        notes: "COVER on Open terrain until move. Can use as 2-Handed Melee."
      }
    ],
    upgrades: []
  }
];

export const IRON_SULTANATE_WISDOM_MODELS: PremadeModelTemplate[] = [
  // ============================================================================
  // ELITE MODELS
  // ============================================================================
  {
    name: "Jabirean Alchemist",
    class: "Jabirean Alchemist",
    type: "elite",
    cost: 55,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 2,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["SULTANATE", "ELITE"],
    abilities: [
      "Mastery of the Elements: On deploy, choose FIRE/GAS/SHRAPNEL for all weapons.",
      "Elemental Change ACTION: Risky Success to change element keyword on all weapons."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "alchemist_medicine",
        name: "Medicine",
        cost: 15,
        effects: {
          addAbilities: [
            {
              name: "School of Medicine ACTION",
              description: "Success +1 DICE. Remove 2 BLOOD MARKERS or 1 INFECTION MARKER from self/friendly within 1\" OR stand up friendly Down within 1\"."
            }
          ]
        }
      },
      {
        id: "alchemist_cartography",
        name: "Cartography & Geometry",
        cost: 20,
        effects: {
          addAbilities: [
            {
              name: "Cartography & Geometry",
              description: "At game start, give up to 2 models (≤32mm base) INFILTRATOR Keyword."
            }
          ]
        }
      },
      {
        id: "alchemist_takwin_secrets",
        name: "Secrets of Takwin",
        cost: 20,
        effects: {
          addAbilities: [
            {
              name: "Secrets of Takwin",
              description: "If hit while within 1\" of Takwin Homunculus, apply hit to Homunculus instead."
            }
          ]
        }
      },
      {
        id: "alchemist_chemistry",
        name: "Chemistry & Alchemy",
        cost: 25,
        effects: {
          addAbilities: [
            {
              name: "Chemistry & Alchemy",
              description: "At Turn 1 start, set up Elemental Obstacle (up to 2\"x6\", >1\" from terrain, >6\" from models). Choose FIRE/GAS/SHRAPNEL. Difficult & Dangerous terrain with chosen keyword."
            }
          ]
        }
      },
      {
        id: "alchemist_philosophy",
        name: "Philosophy, Poetry and Theology",
        cost: 20,
        effects: {
          addKeywords: ["NEGATE FEAR"]
        }
      }
    ]
  },
  {
    name: "Fāris",
    class: "Fāris",
    type: "elite",
    cost: 55,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 1,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["SULTANATE", "ELITE", "STRONG"],
    abilities: [
      "Counter-Charge: If first ACTION is Charge, add +1 DICE to Melee Attacks for rest of Activation."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: []
  },

  // ============================================================================
  // TROOPER MODELS
  // ============================================================================
  {
    name: "Azeb Kavasse",
    class: "Azeb",
    type: "trooper",
    cost: 25,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: -1,
      ranged: 0,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["SULTANATE"],
    abilities: [],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "azeb_kavasse",
        name: "Kavasse Guardian",
        cost: 5,
        effects: {
          modifyStats: { melee: 1 }
        }
      }
    ]
  },
  {
    name: "Sultanate Sapper",
    class: "Sultanate Sapper",
    type: "trooper",
    cost: 50,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 0,
      ranged: 1,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["SULTANATE"],
    abilities: [
      "Set Mine ACTION: Success +2 DICE → terrain (up to 8\"x8\") becomes Mined.",
      "Defuse Mine: Risky Success to defuse mine when moving into contact.",
      "Detonating Mines: Enemy moving into Mined terrain detonates (SHRAPNEL Injury Roll).",
      "Fortify ACTION: Risky Success → COVER until move.",
      "Forward Positions: Deploy up to 6\" forward in contact with terrain ≥½\" tall."
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "shovel-tool",
        name: "Shovel",
        category: "equipment",
        keywords: [],
        notes: "COVER on Open terrain until move. Can use as 2-Handed Melee."
      }
    ],
    upgrades: []
  },
  {
    name: "Lion of Jabir",
    class: "Lion of Jabir",
    type: "trooper",
    cost: 60,
    costCurrency: "ducats",
    stats: {
      movement: 8,
      melee: 1,
      ranged: 0,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["SULTANATE", "ARTIFICIAL"],
    abilities: [
      "Agile: +1 DICE to Climb/Jump/Diving Charge/Dash.",
      "Artificial Life: Add -1 INJURY DICE to Injury Rolls.",
      "Teeth and Claws: Can make Melee Attack without Melee Weapons.",
      "Pin: Down enemies ≤40mm can't stand if within 1\"."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "lion_fear",
        name: "Fearsome",
        cost: 5,
        effects: {
          addKeywords: ["FEAR"]
        }
      }
    ]
  },
  {
    name: "Takwin Homunculus",
    class: "Takwin Homunculus",
    type: "trooper",
    cost: 40,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 0,
      ranged: 0,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["SULTANATE", "ARTIFICIAL"],
    abilities: [
      "Artificial Life: Add -1 INJURY DICE to Injury Rolls.",
      "Pummelling Blows: Can make Melee Attack without Melee Weapons.",
      "Re-creation: If killed in post-battle, can spend 40 👑 to keep on Roster."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "homunculus_additional_arm",
        name: "Additional Arm",
        cost: 15,
        effects: {
          addAbilities: [
            {
              name: "Additional Arm",
              description: "Make 2 Melee Attacks (one after other) with Fight ACTION."
            }
          ]
        }
      },
      {
        id: "homunculus_elemental_resistance",
        name: "Elemental Resistance",
        cost: 40,
        effects: {
          addKeywords: ["NEGATE FIRE", "NEGATE GAS"]
        }
      },
      {
        id: "homunculus_enslaved_mind",
        name: "Enslaved Mind",
        cost: 10,
        effects: {
          addKeywords: ["FIRETEAM"]
        }
      },
      {
        id: "homunculus_gargantuan",
        name: "Gargantuan Size",
        cost: 20,
        effects: {
          modifyStats: { movement: 2 }
        }
      },
      {
        id: "homunculus_hawk_eyes",
        name: "Hawk Eyes",
        cost: 10,
        effects: {
          modifyStats: { ranged: 1 }
        }
      },
      {
        id: "homunculus_human_hands",
        name: "Human Hands",
        cost: 10,
        effects: {
          addAbilities: [
            {
              name: "Human Hands",
              description: "Can have Ranged/Melee Weapons, Trench Shield or Fire Shield. Cannot use Pummelling Blows if armed with Melee Weapons."
            }
          ]
        }
      },
      {
        id: "homunculus_hypnotic_eyes",
        name: "Hypnotic Eyes",
        cost: 15,
        effects: {
          addAbilities: [
            {
              name: "Hypnotic Eyes",
              description: "Add -1 DICE to Melee Attack Success Rolls targeting this model (except Sniper Priests and BLACK GRAIL models)."
            }
          ]
        }
      },
      {
        id: "homunculus_inhuman_strength",
        name: "Inhuman Strength",
        cost: 15,
        effects: {
          addKeywords: ["STRONG"]
        }
      },
      {
        id: "homunculus_massive_size",
        name: "Massive Size",
        cost: 30,
        effects: {
          addKeywords: ["TOUGH"]
        }
      },
      {
        id: "homunculus_regenerative",
        name: "Regenerative Tissue",
        cost: 25,
        effects: {
          addAbilities: [
            {
              name: "Regenerative Tissue",
              description: "On Activation (before ACTIONS), remove up to 1 BLOOD MARKER."
            }
          ]
        }
      },
      {
        id: "homunculus_solomon_seal",
        name: "Seal of Solomon",
        cost: 10,
        effects: {
          addAbilities: [
            {
              name: "Seal of Solomon",
              description: "Can have Equipment from Iron Sultanate Armoury."
            }
          ]
        }
      },
      {
        id: "homunculus_speed",
        name: "Startling Speed",
        cost: 10,
        effects: {
          addAbilities: [
            {
              name: "Startling Speed",
              description: "Add +1 DICE to Risky Success Roll when taking Dash ACTION."
            }
          ]
        }
      },
      {
        id: "homunculus_terrifying",
        name: "Terrifying Appearance",
        cost: 10,
        effects: {
          addKeywords: ["FEAR"]
        }
      },
      {
        id: "homunculus_two_heads",
        name: "Two Heads",
        cost: 5,
        effects: {
          addAbilities: [
            {
              name: "Two Heads",
              description: "Can have both Hawk Eyes and Hypnotic Eyes."
            }
          ]
        }
      },
      {
        id: "homunculus_wings",
        name: "Wings",
        cost: 30,
        effects: {
          modifyStats: { movement: 8 }
        }
      }
    ]
  }
];

export const IRON_SULTANATE_IRON_WALL_MODELS: PremadeModelTemplate[] = [
  // ============================================================================
  // ELITE MODELS
  // ============================================================================
  {
    name: "Silahdar",
    class: "Silahdar",
    type: "elite",
    cost: 70,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 2,
      ranged: 2,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["SULTANATE", "ELITE", "LEADER", "NEGATE FEAR", "TOUGH", "STRONG"],
    abilities: [
      "Janissary Veteran: +10 ducats for STRONG and Counter-Charge (+1 DICE Melee after Charge)."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "silahdar_commander",
        name: "Field Commander",
        cost: 25,
        effects: {
          modifyStats: { ranged: 1 }
        }
      }
    ]
  },
  {
    name: "Janissary Officer",
    class: "Janissary Officer",
    type: "elite",
    cost: 55,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 1,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["SULTANATE", "STRONG", "ELITE"],
    abilities: [
      "Counter-Charge: If first ACTION is Charge, add +1 DICE to Melee Attacks for rest of Activation.",
      "Siege Jezzail Teams: +1 DICE if friendly within 1\""
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: []
  },
  {
    name: "Jabirean Alchemist",
    class: "Jabirean Alchemist",
    type: "elite",
    cost: 55,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 2,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["SULTANATE", "ELITE"],
    abilities: [
      "Mastery of the Elements: Choose FIRE/GAS/SHRAPNEL for all weapons on deploy.",
      "Elemental Change ACTION: Risky Success to change element."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: []
  },
  {
    name: "Brazen Bull",
    class: "Brazen Bull",
    type: "elite",
    cost: 100,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 2,
      ranged: 0,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["SULTANATE", "ARTIFICIAL", "FEAR", "STRONG", "TOUGH"],
    abilities: [
      "Artificial Life: -1 INJURY DICE.",
      "Trample ACTION: Melee vs Down with IGNORE ARMOUR."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: []
  },

  // ============================================================================
  // TROOPER MODELS
  // ============================================================================
  {
    name: "Azeb",
    class: "Azeb",
    type: "trooper",
    cost: 25,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: -1,
      ranged: 0,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["SULTANATE"],
    abilities: [
      "Marksmanship: +2 DICE for Elevated Position instead of +1."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: []
  },
  {
    name: "Sultanate Sapper",
    class: "Sultanate Sapper",
    type: "trooper",
    cost: 50,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 0,
      ranged: 1,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["SULTANATE"],
    abilities: [
      "Set Mine ACTION: Success +2 DICE → terrain (up to 8\"x8\") becomes Mined.",
      "Defuse Mine: Risky Success to defuse mine when moving into contact.",
      "Detonating Mines: Enemy moving into Mined terrain detonates (SHRAPNEL Injury Roll).",
      "Fortify ACTION: Risky Success → COVER until move.",
      "Forward Positions: Deploy up to 6\" forward in contact with terrain ≥½\" tall.",
      "Siege Jezzail Teams: +1 DICE if friendly within 1\""
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "shovel-tool",
        name: "Shovel",
        category: "equipment",
        keywords: [],
        notes: "COVER on Open terrain until move. Can use as 2-Handed Melee."
      }
    ],
    upgrades: []
  },
  {
    name: "Dervish",
    class: "Dervish",
    type: "trooper",
    cost: 55,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 1,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["SULTANATE", "STRONG", "IGNORE OFF-HAND WEAPON"],
    abilities: [
      "Whirling Dervish: Add -1 DICE to Success Rolls for Ranged Attacks targeting this model.",
      "Counter-Charge: If first ACTION is Charge, add +1 DICE to Melee Attacks for rest of Activation.",
      "Marksmanship: +2 DICE for Elevated Position instead of +1."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: []
  },
  {
    name: "Sipahi Automaton Cavalry",
    class: "Sipahi Automaton Cavalry",
    type: "trooper",
    cost: 110,
    costCurrency: "ducats",
    stats: {
      movement: 8,
      melee: 1,
      ranged: 1,
      baseArmour: 1,
      finalArmour: 1
    },
    keywords: ["SULTANATE", "ARTIFICIAL", "CAVALRY"],
    abilities: [
      "Artificial Life: -1 INJURY DICE.",
      "Charge Bonus: D6\" instead of D3\".",
      "Cavalry Impact: Add +1 DICE to Melee Attack if charged 6\"+ this activation.",
      "Trample: Enemy ≤32mm base hit by charge must make Risky Success (D3) or be Down.",
      "Mercenary: Counts as Mercenary for army composition."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: []
  }
];

export const IRON_SULTANATE_IRON_WALL_ITEMS: any[] = [
  // Standard Iron Sultanate items
  ...IRON_SULTANATE_STANDARD_ITEMS,

  // ============================================================================
  // IRON WALL SPECIFIC ITEMS
  // ============================================================================
  {
    name: "Anqā Guard",
    category: "equipment",
    keywords: ["HEAVY"],
    cost: 10,
    costCurrency: "ducats",
    restrictions: ["Sultanate Sappers only"],
    notes: "Deploy at game start in deployment zone OR Combat Deployment ACTION (Risky +2 DICE). Becomes Impassable terrain, Cover/Defended Obstacle."
  },
  {
    name: "Banner of Desert Wind",
    category: "equipment",
    keywords: ["HELD", "LEADER"],
    cost: 30,
    costCurrency: "ducats",
    limit: 1,
    notes: "Sandstorm: Enemy within 24\" have -1 Movement (unless carrier is Down)."
  },
  {
    name: "Explosive Charges",
    category: "equipment",
    keywords: ["CONSUMABLE"],
    cost: 35,
    costCurrency: "ducats",
    limit: 1,
    restrictions: ["Silahdar & Sultanate Sappers only"],
    notes: "Plant on terrain (≤8\"x8\") at game start, detonate later. SHRAPNEL Injury Roll for all in contact, terrain removed."
  },
  {
    name: "Iron Wall Kalkan",
    category: "shield",
    keywords: ["COVER"],
    cost: 30,
    costCurrency: "ducats",
    limit: 1,
    restrictions: ["Brazen Bull only"],
    notes: "Othismos: Push enemy (≤40mm base) when taking Move ACTION."
  },
  {
    name: "Sultanate Grand Cannon",
    category: "ranged",
    keywords: ["1-HANDED", "+2 INJURY DICE", "HEAVY", "IGNORE ARMOUR"],
    range: "48\"",
    cost: 60,
    costCurrency: "ducats",
    limit: 2,
    notes: "Unstoppable Object: If not Out of Action, push target D6\" away. Can be deployed as terrain (Gun Battery) instead of carried by Brazen Bull."
  }
];

export const NEW_ANTIOCH_MODELS: PremadeModelTemplate[] = [
  // ============================================================================
  // ELITE MODELS
  // ============================================================================
  {
    name: "Lieutenant",
    class: "Lieutenant",
    type: "elite",
    cost: 70,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 2,
      ranged: 2,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH", "ELITE", "LEADER", "TOUGH"],
    abilities: [
      "Hold Your Fire! ACTION: Pick 1 enemy in Line of Sight. Activation ends, opponent must Activate that model."
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "lieutenant-pistol",
        name: "Pistol",
        category: "ranged",
        keywords: ["PISTOL"],
        range: "12\"/Melee",
        notes: ""
      },
      {
        id: "lieutenant-sword",
        name: "Sword/Axe",
        category: "melee",
        keywords: ["CRITICAL"],
        range: "",
        notes: ""
      }
    ],
    upgrades: [],
    limit: 1
  },
  {
    name: "Sniper Priest",
    class: "Sniper Priest",
    type: "elite",
    cost: 50,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: -1,
      ranged: 2,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH", "ELITE"],
    abilities: [
      "Absolute Faith: Opponent cannot spend BLOOD MARKERS to add -DICE to Ranged Attacks by this model.",
      "Aim ACTION: Risky Success +2 DICE. Success: +2 DICE to Ranged Attacks for rest of Activation."
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "sniper-rifle",
        name: "Bolt-Action Rifle",
        category: "ranged",
        keywords: ["2-HANDED", "Bayonet Lug"],
        range: "24\"",
        notes: ""
      }
    ],
    upgrades: [],
    limit: 2
  },
  {
    name: "Trench Cleric",
    class: "Trench Cleric",
    type: "elite",
    cost: 60,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 1,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH", "ELITE", "NEGATE FEAR"],
    abilities: [
      "God is With Us! ACTION: Risky Success. Success: Place 1 BLESSING MARKER on self or friendly within 6\".",
      "Onward Christian Soldiers!: Friendly NEW ANTIOCH within 8\" have NEGATE FEAR."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [],
    limit: 1
  },

  // ============================================================================
  // TROOPER MODELS
  // ============================================================================
  {
    name: "Yeoman",
    class: "Yeoman",
    type: "trooper",
    cost: 30,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 0,
      ranged: 0,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH"],
    abilities: [],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "yeoman-rifle",
        name: "Bolt-Action Rifle",
        category: "ranged",
        keywords: ["2-HANDED", "Bayonet Lug"],
        range: "24\"",
        notes: ""
      }
    ],
    upgrades: [
      {
        id: "yeoman_trench_mole",
        name: "Trench Mole",
        cost: 10,
        limit: 3,
        effects: {
          addKeywords: ["INFILTRATOR"]
        }
      }
    ]
  },
  {
    name: "Shock Trooper",
    class: "Shock Trooper",
    type: "trooper",
    cost: 45,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 0,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH"],
    abilities: [
      "Assault Drill: Ignore HEAVY Keyword for 1 Melee Weapon (still limited to 1 HEAVY Weapon total).",
      "Shock Charge: Roll 1 extra D6 for Charge Bonus, use single highest die."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [],
    limit: 5
  },
  {
    name: "Combat Engineer",
    class: "Combat Engineer",
    type: "trooper",
    cost: 80,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 0,
      baseArmour: -2,
      finalArmour: -2
    },
    keywords: ["NEW ANTIOCH"],
    abilities: [
      "Battlefield Demolition: Ignore HEAVY Keyword for 1 Satchel Charge (still limited to 1 HEAVY Weapon total).",
      "Defuse Mine: Risky Success to defuse when moving into mined terrain.",
      "Fortify ACTION: Risky Success → COVER until move."
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "engineer-armour",
        name: "Engineer Body Armour",
        category: "armour",
        keywords: ["NEGATE SHRAPNEL"],
        armourModifier: "-2 INJURY MODIFIER",
        notes: "Ballistic Box Armour: -1 INJURY DICE vs SHRAPNEL."
      },
      {
        id: "engineer-shovel",
        name: "Shovel",
        category: "equipment",
        keywords: [],
        notes: "COVER on Open terrain until move. Can use as 2-Handed Melee."
      }
    ],
    upgrades: [],
    limit: 2
  },
  {
    name: "Mechanized Heavy Infantry",
    class: "Mechanized Heavy Infantry",
    type: "trooper",
    cost: 85,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 0,
      ranged: 1,
      baseArmour: -2,
      finalArmour: -2
    },
    keywords: ["NEW ANTIOCH", "STRONG"],
    abilities: [],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "mech_heavy_machine_armour",
        name: "Machine Armour",
        cost: 10,
        effects: {
          modifyStats: { baseArmour: -3, finalArmour: -3 }
        }
      }
    ],
    limit: 3
  },
  {
    name: "Combat Medic",
    class: "Combat Medic",
    type: "trooper",
    cost: 65,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 0,
      ranged: 0,
      baseArmour: -1,
      finalArmour: -1
    },
    keywords: ["NEW ANTIOCH", "NEGATE FEAR"],
    abilities: [
      "Expert Medic: Add +2 DICE to Risky Success Roll when using Treat ACTION.",
      "Finish the Fallen: Add +1 DICE to Injury Rolls for Melee Attacks vs Down targets (unless BLACK GRAIL or DEMONIC)."
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "medic-armour",
        name: "Standard Armour",
        category: "armour",
        keywords: [],
        armourModifier: "-1 INJURY MODIFIER",
        notes: ""
      },
      {
        id: "medic-gas-mask",
        name: "Gas Mask",
        category: "equipment",
        keywords: ["NEGATE GAS"],
        notes: ""
      },
      {
        id: "medic-medi-kit",
        name: "Medi-kit",
        category: "equipment",
        keywords: [],
        notes: "Treat ACTION: Risky Success to remove BLOOD MARKER or stand up Down model within 1\"."
      },
      {
        id: "medic-misericordia",
        name: "Misericordia",
        category: "melee",
        keywords: [],
        range: "",
        notes: "Despatch: This weapon has the IGNORE ARMOUR Keyword if the target is Down."
      }
    ],
    upgrades: [],
    limit: 1
  }
];

export const NEW_ANTIOCH_ITEMS: any[] = [
  // ============================================================================
  // RANGED WEAPONS
  // ============================================================================
  {
    name: "Automatic Pistol",
    category: "ranged",
    keywords: ["1-HANDED", "ASSAULT", "AUTOMATIC 2", "PISTOL"],
    range: "12\"/Melee",
    cost: 20,
    costCurrency: "ducats",
    limit: 3,
    restrictions: ["ELITE only"],
    notes: ""
  },
  {
    name: "Automatic Rifle",
    category: "ranged",
    keywords: ["2-HANDED", "ASSAULT", "AUTOMATIC 2", "Bayonet Lug"],
    range: "24\"",
    cost: 40,
    costCurrency: "ducats",
    limit: 1,
    notes: "Focused Fire: When this Weapon is used to make 2 Ranged Attacks, the same enemy model must be the target of both attacks."
  },
  {
    name: "Automatic Shotgun",
    category: "ranged",
    keywords: ["2-HANDED", "+1 DICE", "ASSAULT", "SHOTGUN", "Bayonet Lug", "Shield Combo"],
    range: "12\"",
    cost: 15,
    costCurrency: "ducats",
    limit: 2,
    notes: ""
  },
  {
    name: "Bolt-Action Rifle",
    category: "ranged",
    keywords: ["2-HANDED", "Bayonet Lug"],
    range: "24\"",
    cost: 10,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Bolt-Action Rifle (Yeoman)",
    category: "ranged",
    keywords: ["2-HANDED", "Bayonet Lug"],
    range: "24\"",
    cost: 5,
    costCurrency: "ducats",
    restrictions: ["Yeomen only"],
    notes: "Pre-equipped on Yeoman (included in 30 👑 cost)."
  },
  {
    name: "Flamethrower",
    category: "ranged",
    keywords: ["2-HANDED", "-1 INJURY DICE", "FIRE", "FLAMETHROWER", "IGNORE ARMOUR"],
    range: "8\"",
    cost: 30,
    costCurrency: "ducats",
    limit: 3,
    notes: ""
  },
  {
    name: "Grenade Launcher",
    category: "ranged",
    keywords: ["2-HANDED", "BLAST 3\"", "HEAVY", "IGNORE COVER", "SHRAPNEL"],
    range: "36\"",
    cost: 30,
    costCurrency: "ducats",
    limit: 2,
    notes: ""
  },
  {
    name: "Heavy Flamethrower",
    category: "ranged",
    keywords: ["2-HANDED", "-1 INJURY DICE", "AUTOMATIC 2", "FIRE", "FLAMETHROWER", "HEAVY", "IGNORE ARMOUR"],
    range: "10\"",
    cost: 55,
    costCurrency: "ducats",
    limit: 1,
    notes: ""
  },
  {
    name: "Heavy Shotgun",
    category: "ranged",
    keywords: ["2-HANDED", "+1 DICE", "+1 INJURY DICE", "HEAVY", "SHOTGUN", "Shield Combo"],
    range: "12\"",
    cost: 20,
    costCurrency: "ducats",
    limit: 2,
    notes: ""
  },
  {
    name: "Machine Gun",
    category: "ranged",
    keywords: ["2-HANDED", "AUTOMATIC 3", "HEAVY", "RELOAD"],
    range: "36\"",
    cost: 50,
    costCurrency: "ducats",
    limit: 2,
    notes: ""
  },
  {
    name: "Pistol",
    category: "ranged",
    keywords: ["1-HANDED", "PISTOL"],
    range: "12\"/Melee",
    cost: 6,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Semi-Automatic Rifle",
    category: "ranged",
    keywords: ["2-HANDED", "ASSAULT", "Bayonet Lug"],
    range: "24\"",
    cost: 15,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Shotgun",
    category: "ranged",
    keywords: ["2-HANDED", "+1 DICE", "SHOTGUN", "Bayonet Lug", "Shield Combo"],
    range: "12\"",
    cost: 10,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Sniper Rifle",
    category: "ranged",
    keywords: ["2-HANDED", "+1 DICE", "CRITICAL", "RISKY"],
    range: "48\"",
    cost: 35,
    costCurrency: "ducats",
    limit: 3,
    notes: "Bull's Eye: Critical Success Injury Roll gains IGNORE ARMOUR."
  },
  {
    name: "Submachine Gun",
    category: "ranged",
    keywords: ["2-HANDED", "ASSAULT", "Bayonet Lug", "Shield Combo"],
    range: "16\"",
    cost: 30,
    costCurrency: "ducats",
    limit: 2,
    notes: "Quick Bursts: Can take two Shoot ACTIONS in activation."
  },

  // ============================================================================
  // MELEE WEAPONS
  // ============================================================================
  {
    name: "Bayonet",
    category: "melee",
    keywords: ["1-HANDED", "Shield Combo"],
    range: "Melee",
    cost: 2,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Great Hammer/Maul",
    category: "melee",
    keywords: ["2-HANDED", "+1 INJURY MODIFIER", "HEAVY"],
    range: "Melee",
    cost: 10,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Great Sword/Axe",
    category: "melee",
    keywords: ["2-HANDED", "+1 INJURY DICE", "CRITICAL", "HEAVY"],
    range: "Melee",
    cost: 12,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Misericordia",
    category: "melee",
    keywords: ["1-HANDED"],
    range: "Melee",
    cost: 15,
    costCurrency: "ducats",
    restrictions: ["Combat Medic only"],
    limit: 1,
    notes: "Despatch: IGNORE ARMOUR vs Down target."
  },
  {
    name: "Polearm",
    category: "melee",
    keywords: ["2-HANDED", "BLOCK", "CUMBERSOME", "Shield Combo"],
    range: "Melee",
    cost: 7,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Sword/Axe",
    category: "melee",
    keywords: ["1-HANDED", "CRITICAL"],
    range: "Melee",
    cost: 4,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Trench Club",
    category: "melee",
    keywords: ["1-HANDED"],
    range: "Melee",
    cost: 3,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Trench Knife",
    category: "melee",
    keywords: ["1-HANDED", "-1 DICE"],
    range: "Melee",
    cost: 1,
    costCurrency: "ducats",
    notes: ""
  },

  // ============================================================================
  // GRENADES
  // ============================================================================
  {
    name: "Frag Grenades",
    category: "grenade",
    keywords: ["ASSAULT", "BLAST 2\"", "IGNORE COVER", "IGNORE LONG RANGE", "SHRAPNEL"],
    range: "8\"",
    cost: 7,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Incendiary Grenades",
    category: "grenade",
    keywords: ["ASSAULT", "FIRE", "IGNORE COVER", "IGNORE LONG RANGE"],
    range: "8\"",
    cost: 15,
    costCurrency: "ducats",
    limit: 2,
    notes: "Liquid Fire: Critical Success Injury Roll gains IGNORE ARMOUR."
  },
  {
    name: "Satchel Charge",
    category: "grenade",
    keywords: ["BLAST 3\"", "CONSUMABLE", "HEAVY", "IGNORE ARMOUR", "IGNORE COVER", "SCATTER", "+1 INJURY DICE"],
    range: "6\"",
    cost: 15,
    costCurrency: "ducats",
    limit: 3,
    notes: "Heavy Explosive: Once per game use."
  },

  // ============================================================================
  // SHIELDS
  // ============================================================================
  {
    name: "Heavy Ballistic Shield",
    category: "shield",
    keywords: ["COVER", "Shield Combo"],
    range: "",
    armourModifier: "",
    cost: 15,
    costCurrency: "ducats",
    restrictions: ["Models wearing Machine Armour only"],
    notes: ""
  },
  {
    name: "Trench Shield",
    category: "shield",
    keywords: ["Shield Combo"],
    armourModifier: "-1 INJURY MODIFIER",
    cost: 10,
    costCurrency: "ducats",
    notes: ""
  },

  // ============================================================================
  // ARMOUR
  // ============================================================================
  {
    name: "Engineer Body Armour",
    category: "armour",
    keywords: ["NEGATE SHRAPNEL"],
    armourModifier: "-2 INJURY MODIFIER",
    cost: 45,
    costCurrency: "ducats",
    restrictions: ["Combat Engineer only"],
    notes: "Ballistic Box Armour: -1 INJURY DICE vs SHRAPNEL."
  },
  {
    name: "Machine Armour",
    category: "armour",
    keywords: [],
    armourModifier: "-3 INJURY MODIFIER",
    cost: 50,
    costCurrency: "ducats",
    limit: 1,
    restrictions: ["ELITE & Mechanized Heavy Infantry only"],
    notes: "Bulky: Change base to 40mm. Cannot equip Trench Shield. Charge Bonus D3\" instead of D6\". Standfast: Down result becomes Minor Wound."
  },
  {
    name: "Reinforced Armour",
    category: "armour",
    keywords: [],
    armourModifier: "-2 INJURY MODIFIER",
    cost: 40,
    costCurrency: "ducats",
    restrictions: ["ELITE & Mechanized Heavy Infantry only"],
    notes: ""
  },
  {
    name: "Standard Armour",
    category: "armour",
    keywords: [],
    armourModifier: "-1 INJURY MODIFIER",
    cost: 15,
    costCurrency: "ducats",
    notes: ""
  },

  // ============================================================================
  // EQUIPMENT
  // ============================================================================
  {
    name: "Binoculars",
    category: "equipment",
    keywords: [],
    cost: 10,
    costCurrency: "ducats",
    restrictions: ["ELITE only"],
    notes: "INFILTRATOR cannot deploy within 16\" unless in own zone."
  },
  {
    name: "Combat Helmet",
    category: "equipment",
    keywords: ["NEGATE SHRAPNEL"],
    cost: 5,
    costCurrency: "ducats",
    notes: "Headgear"
  },
  {
    name: "Field Shrine",
    category: "equipment",
    keywords: [],
    cost: 2,
    costCurrency: "glory",
    notes: "Deploy terrain piece. +3 models for Morale (max +9). Can be attacked."
  },
  {
    name: "Gas Mask",
    category: "equipment",
    keywords: ["NEGATE GAS"],
    cost: 5,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Martyrdom Pills",
    category: "equipment",
    keywords: ["CONSUMABLE"],
    cost: 1,
    costCurrency: "glory",
    limit: 2,
    restrictions: ["ELITE only"],
    notes: "Consume on deploy: NEGATE FEAR + -1 INJURY DICE all game."
  },
  {
    name: "Medi-kit",
    category: "equipment",
    keywords: [],
    cost: 5,
    costCurrency: "ducats",
    notes: "Treat ACTION: Risky Success to remove BLOOD MARKER or stand up Down model within 1\"."
  },
  {
    name: "Mountaineer Kit",
    category: "equipment",
    keywords: [],
    cost: 3,
    costCurrency: "ducats",
    limit: 4,
    notes: "+1 DICE to climb sheer surfaces."
  },
  {
    name: "Musical Instrument",
    category: "equipment",
    keywords: ["HELD"],
    cost: 15,
    costCurrency: "ducats",
    limit: 1,
    notes: "+1 DICE to Dash for friendly within 4\"."
  },
  {
    name: "Shovel",
    category: "equipment",
    keywords: [],
    cost: 5,
    costCurrency: "ducats",
    notes: "COVER on Open terrain until move. Can use as 2-Handed Melee."
  },
  {
    name: "Troop Flag",
    category: "equipment",
    keywords: ["LEADER", "HELD"],
    cost: 1,
    costCurrency: "glory",
    limit: 1,
    notes: ""
  }
];

export const NEW_ANTIOCH_PAPAL_MODELS: PremadeModelTemplate[] = [
  // ============================================================================
  // ELITE MODELS
  // ============================================================================
  {
    name: "Lieutenant",
    class: "Lieutenant",
    type: "elite",
    cost: 70,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 2,
      ranged: 2,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH", "ELITE", "LEADER", "NEGATE FEAR"],
    abilities: [],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "lieutenant_swiss_guard",
        name: "Swiss Guard",
        cost: 0,
        effects: {
          addKeywords: []
        }
      }
    ],
    limit: 1
  },
  {
    name: "Sniper Priest",
    class: "Sniper Priest",
    type: "elite",
    cost: 50,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: -1,
      ranged: 2,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH", "ELITE"],
    abilities: [
      "Absolute Faith: Opponent cannot spend BLOOD MARKERS to add -DICE to Ranged Attacks by this model.",
      "Aim ACTION: Risky Success +2 DICE. Success: +2 DICE to Ranged Attacks for rest of Activation."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "sniper_swiss_guard",
        name: "Swiss Guard",
        cost: 0,
        effects: {
          addKeywords: ["NEGATE FEAR"]
        }
      }
    ],
    limit: 2
  },
  {
    name: "Lector",
    class: "Lector",
    type: "elite",
    cost: 60,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 1,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH", "ELITE", "NEGATE FEAR"],
    abilities: [
      "Arise and be Healed! RISKY ACTION: On a Success a model within 3\" stands up and can remove up to D3 BLOOD MARKERS and/or INFECTION MARKERS."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "lector_swiss_guard",
        name: "Swiss Guard",
        cost: 0,
        effects: {
          addKeywords: []
        }
      }
    ],
    limit: 1
  },

  // ============================================================================
  // TROOPER MODELS
  // ============================================================================
  {
    name: "Yeoman",
    class: "Yeoman",
    type: "trooper",
    cost: 30,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 0,
      ranged: 0,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH"],
    abilities: [],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "yeoman-rifle",
        name: "Bolt-Action Rifle",
        category: "ranged",
        keywords: ["2-HANDED", "Bayonet Lug"],
        range: "24\"",
        notes: ""
      }
    ],
    upgrades: [
      {
        id: "yeoman_swiss_guard",
        name: "Swiss Guard",
        cost: 0,
        effects: {
          addKeywords: ["NEGATE FEAR"]
        }
      }
    ]
  },
  {
    name: "Shock Trooper",
    class: "Shock Trooper",
    type: "trooper",
    cost: 45,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 0,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH"],
    abilities: [
      "Assault Drill: Ignore HEAVY Keyword for 1 Melee Weapon (still limited to 1 HEAVY Weapon total).",
      "Shock Charge: Roll 1 extra D6 for Charge Bonus, use single highest die."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "shock_swiss_guard",
        name: "Swiss Guard",
        cost: 0,
        effects: {
          addKeywords: ["NEGATE FEAR"]
        }
      }
    ],
    limit: 5
  },
  {
    name: "Combat Engineer",
    class: "Combat Engineer",
    type: "trooper",
    cost: 80,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 0,
      baseArmour: -2,
      finalArmour: -2
    },
    keywords: ["NEW ANTIOCH"],
    abilities: [
      "Battlefield Demolition: Ignore HEAVY Keyword for 1 Satchel Charge (still limited to 1 HEAVY Weapon total).",
      "Defuse Mine: Risky Success to defuse when moving into mined terrain.",
      "Fortify ACTION: Risky Success → COVER until move."
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "engineer-armour",
        name: "Engineer Body Armour",
        category: "armour",
        keywords: ["NEGATE SHRAPNEL"],
        armourModifier: "-2 INJURY MODIFIER",
        notes: "Ballistic Box Armour: -1 INJURY DICE vs SHRAPNEL."
      },
      {
        id: "engineer-shovel",
        name: "Shovel",
        category: "equipment",
        keywords: [],
        notes: "COVER on Open terrain until move. Can use as 2-Handed Melee."
      }
    ],
    upgrades: [
      {
        id: "engineer_swiss_guard",
        name: "Swiss Guard",
        cost: 0,
        effects: {
          addKeywords: ["NEGATE FEAR"]
        }
      }
    ],
    limit: 2
  },
  {
    name: "Mechanized Heavy Infantry",
    class: "Mechanized Heavy Infantry",
    type: "trooper",
    cost: 85,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 0,
      ranged: 1,
      baseArmour: -2,
      finalArmour: -2
    },
    keywords: ["NEW ANTIOCH", "STRONG"],
    abilities: [],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "mhi_machine_armour",
        name: "Machine Armour",
        cost: 10,
        effects: {
          modifyStats: { baseArmour: -3, finalArmour: -3 }
        }
      },
      {
        id: "mhi_swiss_guard",
        name: "Swiss Guard",
        cost: 0,
        effects: {
          addKeywords: ["NEGATE FEAR"]
        }
      }
    ],
    limit: 3
  },
  {
    name: "Combat Medic",
    class: "Combat Medic",
    type: "trooper",
    cost: 65,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 0,
      ranged: 0,
      baseArmour: -1,
      finalArmour: -1
    },
    keywords: ["NEW ANTIOCH", "NEGATE FEAR"],
    abilities: [
      "Expert Medic: Add +2 DICE to Risky Success Roll when using Treat ACTION.",
      "Finish the Fallen: Add +1 DICE to Injury Rolls for Melee Attacks vs Down targets (unless BLACK GRAIL or DEMONIC)."
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "medic-armour",
        name: "Standard Armour",
        category: "armour",
        keywords: [],
        armourModifier: "-1 INJURY MODIFIER",
        notes: ""
      },
      {
        id: "medic-gas-mask",
        name: "Gas Mask",
        category: "equipment",
        keywords: ["NEGATE GAS"],
        notes: ""
      },
      {
        id: "medic-medi-kit",
        name: "Medi-kit",
        category: "equipment",
        keywords: [],
        notes: "Treat ACTION: Risky Success to remove BLOOD MARKER or stand up Down model within 1\"."
      },
      {
        id: "medic-misericordia",
        name: "Misericordia",
        category: "melee",
        keywords: [],
        range: "",
        notes: "Despatch: This weapon has the IGNORE ARMOUR Keyword if the target is Down."
      }
    ],
    upgrades: [
      {
        id: "medic_swiss_guard",
        name: "Swiss Guard",
        cost: 0,
        effects: {
          addKeywords: []
        }
      }
    ],
    limit: 1
  }
];

export const NEW_ANTIOCH_EIRE_MODELS: PremadeModelTemplate[] = [
  // ============================================================================
  // ELITE MODELS
  // ============================================================================
  {
    name: "Lieutenant",
    class: "Lieutenant",
    type: "elite",
    cost: 70,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 2,
      ranged: 2,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH", "ELITE", "LEADER", "SKIRMISHER"],
    abilities: [],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "lieutenant_berserker",
        name: "Berserker",
        cost: 0,
        effects: {
          addKeywords: ["NEGATE FEAR"]
        }
      }
    ],
    limit: 1
  },
  {
    name: "Sniper Priest",
    class: "Sniper Priest",
    type: "elite",
    cost: 50,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: -1,
      ranged: 2,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH", "ELITE"],
    abilities: [
      "Absolute Faith: Opponent cannot spend BLOOD MARKERS to add -DICE to Ranged Attacks by this model.",
      "Aim ACTION: Risky Success +2 DICE. Success: +2 DICE to Ranged Attacks for rest of Activation."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [],
    limit: 2
  },
  {
    name: "Trench Cleric",
    class: "Trench Cleric",
    type: "elite",
    cost: 60,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 1,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH", "ELITE"],
    abilities: [],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "cleric_arise_healed",
        name: "Arise and be Healed!",
        cost: 0,
        effects: {
          addAbilities: [
            {
              name: "Arise and be Healed!",
              description: "RISKY ACTION: On a Success a model within 3\" stands up and can remove up to D3 BLOOD MARKERS and/or INFECTION MARKERS."
            }
          ]
        }
      },
      {
        id: "cleric_away_serpents",
        name: "Away Serpents!",
        cost: 0,
        effects: {
          addAbilities: [
            {
              name: "Away Serpents!",
              description: "RISKY ACTION: Pick a model within 12\". Add -1 DICE if the enemy's base size is 40mm or bigger. On a Success the enemy is taken down."
            }
          ]
        }
      },
      {
        id: "cleric_negate_fear",
        name: "Onward Christian Soldiers!",
        cost: 0,
        effects: {
          addKeywords: ["NEGATE FEAR"]
        }
      }
    ],
    limit: 2
  },

  // ============================================================================
  // TROOPER MODELS
  // ============================================================================
  {
    name: "Yeoman",
    class: "Yeoman",
    type: "trooper",
    cost: 30,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 0,
      ranged: 0,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH"],
    abilities: [],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "yeoman-rifle",
        name: "Bolt-Action Rifle",
        category: "ranged",
        keywords: ["2-HANDED", "Bayonet Lug"],
        range: "24\"",
        notes: ""
      }
    ],
    upgrades: [
      {
        id: "yeoman_trench_mole",
        name: "Trench Mole",
        cost: 10,
        limit: 3,
        effects: {
          addKeywords: ["INFILTRATOR"]
        }
      }
    ]
  },
  {
    name: "Shock Trooper",
    class: "Shock Trooper",
    type: "trooper",
    cost: 45,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 0,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH"],
    abilities: [
      "Assault Drill: Ignore HEAVY Keyword for 1 Melee Weapon (still limited to 1 HEAVY Weapon total).",
      "Shock Charge: Roll 1 extra D6 for Charge Bonus, use single highest die."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "shock_fianna",
        name: "Fianna",
        cost: 0,
        effects: {
          addKeywords: ["INFILTRATOR", "SKIRMISHER"]
        }
      },
      {
        id: "shock_berserker",
        name: "Berserker",
        cost: 0,
        effects: {
          addKeywords: ["NEGATE FEAR"]
        }
      }
    ],
    limit: 5
  },
  {
    name: "Combat Engineer",
    class: "Combat Engineer",
    type: "trooper",
    cost: 80,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 0,
      baseArmour: -2,
      finalArmour: -2
    },
    keywords: ["NEW ANTIOCH"],
    abilities: [
      "Battlefield Demolition: Ignore HEAVY Keyword for 1 Satchel Charge (still limited to 1 HEAVY Weapon total).",
      "Defuse Mine: Risky Success to defuse when moving into mined terrain.",
      "Fortify ACTION: Risky Success → COVER until move."
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "engineer-armour",
        name: "Engineer Body Armour",
        category: "armour",
        keywords: ["NEGATE SHRAPNEL"],
        armourModifier: "-2 INJURY MODIFIER",
        notes: "Ballistic Box Armour: -1 INJURY DICE vs SHRAPNEL."
      },
      {
        id: "engineer-shovel",
        name: "Shovel",
        category: "equipment",
        keywords: [],
        notes: "COVER on Open terrain until move. Can use as 2-Handed Melee."
      }
    ],
    upgrades: [],
    limit: 2
  },
  {
    name: "Mechanized Heavy Infantry",
    class: "Mechanized Heavy Infantry",
    type: "trooper",
    cost: 85,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 0,
      ranged: 1,
      baseArmour: -3,
      finalArmour: -3
    },
    keywords: ["NEW ANTIOCH", "STRONG"],
    abilities: [],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "mhi-machine-armour",
        name: "Machine Armour",
        category: "armour",
        keywords: [],
        armourModifier: "-3 INJURY MODIFIER",
        notes: "Bulky: Change base to 40mm. Cannot equip Trench Shield. Charge Bonus D3\" instead of D6\". Standfast: Down result becomes Minor Wound."
      }
    ],
    upgrades: [],
    limit: 1
  },
  {
    name: "Combat Medic",
    class: "Combat Medic",
    type: "trooper",
    cost: 65,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 0,
      ranged: 0,
      baseArmour: -1,
      finalArmour: -1
    },
    keywords: ["NEW ANTIOCH", "NEGATE FEAR"],
    abilities: [
      "Expert Medic: Add +2 DICE to Risky Success Roll when using Treat ACTION.",
      "Finish the Fallen: Add +1 DICE to Injury Rolls for Melee Attacks vs Down targets (unless BLACK GRAIL or DEMONIC)."
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "medic-armour",
        name: "Standard Armour",
        category: "armour",
        keywords: [],
        armourModifier: "-1 INJURY MODIFIER",
        notes: ""
      },
      {
        id: "medic-gas-mask",
        name: "Gas Mask",
        category: "equipment",
        keywords: ["NEGATE GAS"],
        notes: ""
      },
      {
        id: "medic-medi-kit",
        name: "Medi-kit",
        category: "equipment",
        keywords: [],
        notes: "Treat ACTION: Risky Success to remove BLOOD MARKER or stand up Down model within 1\"."
      },
      {
        id: "medic-misericordia",
        name: "Misericordia",
        category: "melee",
        keywords: [],
        range: "",
        notes: "Despatch: This weapon has the IGNORE ARMOUR Keyword if the target is Down."
      }
    ],
    upgrades: [],
    limit: 1
  }
];

export const NEW_ANTIOCH_PAPAL_ITEMS: any[] = [
  ...NEW_ANTIOCH_ITEMS,
  {
    name: "Supreme Pontiff's Crucifix",
    category: "equipment",
    keywords: [],
    cost: 3,
    costCurrency: "glory",
    limit: 1,
    notes: "Inspiring Relic: The bearer of this relic does not end their activation after a failed Risky Success Roll."
  }
];

export const NEW_ANTIOCH_EIRE_ITEMS: any[] = [
  ...NEW_ANTIOCH_ITEMS.filter(item => item.name !== "Satchel Charge"),
  {
    name: "Armour Piercing Bullets",
    category: "equipment",
    keywords: ["ARMOUR PIERCE"],
    cost: 5,
    costCurrency: "ducats",
    limit: 2,
    notes: ""
  },
  {
    name: "Satchel Charge",
    category: "grenade",
    keywords: ["BLAST 3\"", "CONSUMABLE", "HEAVY", "IGNORE ARMOUR", "IGNORE COVER", "SCATTER", "+1 INJURY DICE"],
    range: "6\"",
    cost: 15,
    costCurrency: "ducats",
    limit: 4,
    notes: "Heavy Explosive: Once per game use."
  }
];

export const NEW_ANTIOCH_ALBA_MODELS: PremadeModelTemplate[] = [
  // ============================================================================
  // ELITE MODELS
  // ============================================================================
  {
    name: "Lieutenant",
    class: "Lieutenant",
    type: "elite",
    cost: 70,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 2,
      ranged: 2,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH", "ELITE", "LEADER", "TOUGH", "STRONG"],
    abilities: [
      "Hold Your Fire! ACTION: Pick 1 enemy in Line of Sight. Activation ends, opponent must Activate that model.",
      "Rampant Charge: IGNORE DEFENDED OBSTACLE",
      "Highland Strength: STRONG keyword"
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [],
    limit: 1
  },
  {
    name: "Sniper Priest",
    class: "Sniper Priest",
    type: "elite",
    cost: 50,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: -1,
      ranged: 2,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH", "ELITE"],
    abilities: [
      "Absolute Faith: Opponent cannot spend BLOOD MARKERS to add -DICE to Ranged Attacks by this model.",
      "Aim ACTION: Risky Success +2 DICE. Success: +2 DICE to Ranged Attacks for rest of Activation.",
      "Rampant Charge: IGNORE DEFENDED OBSTACLE",
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [],
    limit: 2
  },
  {
    name: "Trench Cleric",
    class: "Trench Cleric",
    type: "elite",
    cost: 60,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 1,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH", "ELITE", "NEGATE FEAR"],
    abilities: [
      "God is With Us! ACTION: Risky Success. Success: Place 1 BLESSING MARKER on self or friendly within 6\".",
      "Onward Christian Soldiers!: Friendly NEW ANTIOCH within 8\" have NEGATE FEAR.",
      "Rampant Charge: IGNORE DEFENDED OBSTACLE",
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [],
    limit: 1
  },

  // ============================================================================
  // TROOPER MODELS
  // ============================================================================
  {
    name: "Yeoman",
    class: "Yeoman",
    type: "trooper",
    cost: 30,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 0,
      ranged: 0,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH"],
    abilities: [
      "Rampant Charge: IGNORE DEFENDED OBSTACLE",
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "yeoman-rifle",
        name: "Bolt-Action Rifle",
        category: "ranged",
        keywords: ["2-HANDED", "Bayonet Lug"],
        range: "24\"",
        notes: ""
      }
    ],
    upgrades: [
      {
        id: "yeoman_trench_mole",
        name: "Trench Mole",
        cost: 10,
        limit: 3,
        effects: {
          addKeywords: ["INFILTRATOR"]
        }
      }
    ]
  },
  {
    name: "Shock Trooper",
    class: "Shock Trooper",
    type: "trooper",
    cost: 45,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 0,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH", "STRONG"],
    abilities: [
      "Shock Charge: Roll 1 extra D6 for Charge Bonus, use single highest die.",
      "Rampant Charge: IGNORE DEFENDED OBSTACLE",
      "Highland Strength: STRONG keyword"
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [],
    limit: 5
  },
  {
    name: "Combat Engineer",
    class: "Combat Engineer",
    type: "trooper",
    cost: 80,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 0,
      baseArmour: -2,
      finalArmour: -2
    },
    keywords: ["NEW ANTIOCH"],
    abilities: [
      "Battlefield Demolition: Ignore HEAVY Keyword for 1 Satchel Charge (still limited to 1 HEAVY Weapon total).",
      "Defuse Mine: Risky Success to defuse when moving into mined terrain.",
      "Fortify ACTION: Risky Success → COVER until move.",
      "Rampant Charge: IGNORE DEFENDED OBSTACLE",
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "engineer-armour",
        name: "Engineer Body Armour",
        category: "armour",
        keywords: ["NEGATE SHRAPNEL"],
        armourModifier: "-2 INJURY MODIFIER",
        notes: "Ballistic Box Armour: -1 INJURY DICE vs SHRAPNEL."
      },
      {
        id: "engineer-shovel",
        name: "Shovel",
        category: "equipment",
        keywords: [],
        notes: "COVER on Open terrain until move. Can use as 2-Handed Melee."
      }
    ],
    upgrades: [],
    limit: 2
  },
  {
    name: "Mechanized Heavy Infantry",
    class: "Mechanized Heavy Infantry",
    type: "trooper",
    cost: 85,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 0,
      baseArmour: -2,
      finalArmour: -2
    },
    keywords: ["NEW ANTIOCH", "STRONG"],
    abilities: [
      "Rampant Charge: IGNORE DEFENDED OBSTACLE",
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "mhi_machine_armour",
        name: "Machine Armour",
        cost: 10,
        effects: {
          modifyStats: { baseArmour: -3, finalArmour: -3 }
        }
      }
    ],
    limit: 3
  },
  {
    name: "Combat Medic",
    class: "Combat Medic",
    type: "trooper",
    cost: 65,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 0,
      ranged: 0,
      baseArmour: -1,
      finalArmour: -1
    },
    keywords: ["NEW ANTIOCH", "NEGATE FEAR"],
    abilities: [
      "Expert Medic: Add +2 DICE to Risky Success Roll when using Treat ACTION.",
      "Finish the Fallen: Add +1 DICE to Injury Rolls for Melee Attacks vs Down targets (unless BLACK GRAIL or DEMONIC).",
      "Rampant Charge: IGNORE DEFENDED OBSTACLE",
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "medic-armour",
        name: "Standard Armour",
        category: "armour",
        keywords: [],
        armourModifier: "-1 INJURY MODIFIER",
        notes: ""
      },
      {
        id: "medic-gas-mask",
        name: "Gas Mask",
        category: "equipment",
        keywords: ["NEGATE GAS"],
        notes: ""
      },
      {
        id: "medic-medi-kit",
        name: "Medi-kit",
        category: "equipment",
        keywords: [],
        notes: "Treat ACTION: Risky Success to remove BLOOD MARKER or stand up Down model within 1\"."
      },
      {
        id: "medic-misericordia",
        name: "Misericordia",
        category: "melee",
        keywords: [],
        range: "",
        notes: "Despatch: This weapon has the IGNORE ARMOUR Keyword if the target is Down."
      }
    ],
    upgrades: [],
    limit: 1
  }
];

export const NEW_ANTIOCH_PRUSSIA_MODELS: PremadeModelTemplate[] = [
  // ============================================================================
  // ELITE MODELS
  // ============================================================================
  {
    name: "Lieutenant",
    class: "Lieutenant",
    type: "elite",
    cost: 70,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 2,
      ranged: 2,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH", "ELITE", "LEADER", "TOUGH"],
    abilities: [
      "Hold Your Fire! ACTION: Pick 1 enemy in Line of Sight. Activation ends, opponent must Activate that model.",
      "Masters of the Grenade: Add +4\" to Range of all Grenades"
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "lieutenant_rapid_assault",
        name: "Rapid Assault",
        cost: 5,
        effects: {
          addAbilities: [
            {
              name: "Rapid Assault",
              description: "Add +1 DICE to Risky Success Roll when taking Dash ACTION."
            }
          ]
        }
      }
    ],
    limit: 1
  },
  {
    name: "Sniper Priest",
    class: "Sniper Priest",
    type: "elite",
    cost: 50,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: -1,
      ranged: 2,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH", "ELITE"],
    abilities: [
      "Absolute Faith: Opponent cannot spend BLOOD MARKERS to add -DICE to Ranged Attacks by this model.",
      "Aim ACTION: Risky Success +2 DICE. Success: +2 DICE to Ranged Attacks for rest of Activation.",
      "Masters of the Grenade: Add +4\" to Range of all Grenades"
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [],
    limit: 1
  },
  {
    name: "Trench Cleric",
    class: "Trench Cleric",
    type: "elite",
    cost: 60,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 1,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH", "ELITE", "NEGATE FEAR"],
    abilities: [
      "God is With Us! ACTION: Risky Success. Success: Place 1 BLESSING MARKER on self or friendly within 6\".",
      "Onward Christian Soldiers!: Friendly NEW ANTIOCH within 8\" have NEGATE FEAR.",
      "Masters of the Grenade: Add +4\" to Range of all Grenades"
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "cleric_holy_smoke",
        name: "Holy Smoke",
        cost: 0,
        effects: {
          addAbilities: [
            {
              name: "Holy Smoke",
              description: "On deploy, consume to gain NEGATE FEAR and -1 INJURY DICE to all attacks hitting model for rest of game."
            }
          ]
        }
      }
    ],
    limit: 1
  },

  // ============================================================================
  // TROOPER MODELS
  // ============================================================================
  {
    name: "Yeoman",
    class: "Yeoman",
    type: "trooper",
    cost: 30,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 0,
      ranged: 0,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH"],
    abilities: [
      "Masters of the Grenade: Add +4\" to Range of all Grenades"
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "yeoman-rifle",
        name: "Bolt-Action Rifle",
        category: "ranged",
        keywords: ["2-HANDED", "Bayonet Lug"],
        range: "24\"",
        notes: ""
      }
    ],
    upgrades: [],
    limit: 0
  },
  {
    name: "Shock Trooper",
    class: "Shock Trooper",
    type: "trooper",
    cost: 45,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 0,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH"],
    abilities: [
      "Shock Charge: Roll 1 extra D6 for Charge Bonus, use single highest die.",
      "Masters of the Grenade: Add +4\" to Range of all Grenades"
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "shock_infiltrator",
        name: "INFILTRATOR",
        cost: 10,
        limit: 2,
        effects: {
          addKeywords: ["INFILTRATOR"]
        }
      },
      {
        id: "shock_rapid_assault",
        name: "Rapid Assault",
        cost: 5,
        effects: {
          addAbilities: [
            {
              name: "Rapid Assault",
              description: "Add +1 DICE to Risky Success Roll when taking Dash ACTION."
            }
          ]
        }
      }
    ],
    limit: 8
  },
  {
    name: "Combat Engineer",
    class: "Combat Engineer",
    type: "trooper",
    cost: 80,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 0,
      baseArmour: -2,
      finalArmour: -2
    },
    keywords: ["NEW ANTIOCH"],
    abilities: [
      "Battlefield Demolition: Ignore HEAVY Keyword for 1 Satchel Charge (still limited to 1 HEAVY Weapon total).",
      "Defuse Mine: Risky Success to defuse when moving into mined terrain.",
      "Fortify ACTION: Risky Success → COVER until move.",
      "Masters of the Grenade: Add +4\" to Range of all Grenades"
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "engineer-armour",
        name: "Engineer Body Armour",
        category: "armour",
        keywords: ["NEGATE SHRAPNEL"],
        armourModifier: "-2 INJURY MODIFIER",
        notes: "Ballistic Box Armour: -1 INJURY DICE vs SHRAPNEL."
      },
      {
        id: "engineer-shovel",
        name: "Shovel",
        category: "equipment",
        keywords: [],
        notes: "COVER on Open terrain until move. Can use as 2-Handed Melee."
      }
    ],
    upgrades: [],
    limit: 2
  },
  {
    name: "Mechanized Heavy Infantry",
    class: "Mechanized Heavy Infantry",
    type: "trooper",
    cost: 85,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 0,
      ranged: 1,
      baseArmour: -2,
      finalArmour: -2
    },
    keywords: ["NEW ANTIOCH", "STRONG"],
    abilities: [
      "Masters of the Grenade: Add +4\" to Range of all Grenades"
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "mhi_machine_armour",
        name: "Machine Armour",
        cost: 10,
        effects: {
          modifyStats: { baseArmour: -3, finalArmour: -3 }
        }
      }
    ],
    limit: 1
  },
  {
    name: "Combat Medic",
    class: "Combat Medic",
    type: "trooper",
    cost: 65,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 0,
      ranged: 0,
      baseArmour: -1,
      finalArmour: -1
    },
    keywords: ["NEW ANTIOCH", "NEGATE FEAR"],
    abilities: [
      "Expert Medic: Add +2 DICE to Risky Success Roll when using Treat ACTION.",
      "Finish the Fallen: Add +1 DICE to Injury Rolls for Melee Attacks vs Down targets (unless BLACK GRAIL or DEMONIC).",
      "Masters of the Grenade: Add +4\" to Range of all Grenades"
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "medic-armour",
        name: "Standard Armour",
        category: "armour",
        keywords: [],
        armourModifier: "-1 INJURY MODIFIER",
        notes: ""
      },
      {
        id: "medic-gas-mask",
        name: "Gas Mask",
        category: "equipment",
        keywords: ["NEGATE GAS"],
        notes: ""
      },
      {
        id: "medic-medi-kit",
        name: "Medi-kit",
        category: "equipment",
        keywords: [],
        notes: "Treat ACTION: Risky Success to remove BLOOD MARKER or stand up Down model within 1\"."
      },
      {
        id: "medic-misericordia",
        name: "Misericordia",
        category: "melee",
        keywords: [],
        range: "",
        notes: "Despatch: This weapon has the IGNORE ARMOUR Keyword if the target is Down."
      }
    ],
    upgrades: [],
    limit: 1
  }
];

export const NEW_ANTIOCH_ABYSSINIA_MODELS: PremadeModelTemplate[] = [
  // ============================================================================
  // ELITE MODELS
  // ============================================================================
  {
    name: "Lieutenant",
    class: "Lieutenant",
    type: "elite",
    cost: 70,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 2,
      ranged: 2,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH", "ELITE", "LEADER", "TOUGH"],
    abilities: [
      "Hold Your Fire! ACTION: Pick 1 enemy in Line of Sight. Activation ends, opponent must Activate that model.",
      "Short-Range Marksmanship: Add +1 DICE to Ranged Attacks at Short Range (except Grenades or HEAVY weapons)"
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "lieutenant_chewa",
        name: "Chewa",
        cost: 5,
        effects: {
          addAbilities: [
            {
              name: "Chewa",
              description: "Add +1 DICE to Melee Attacks for each friendly within 1\" of target (max +2 DICE)."
            }
          ]
        }
      }
    ],
    limit: 1
  },
  {
    name: "Trench Cleric",
    class: "Trench Cleric",
    type: "elite",
    cost: 60,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 1,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH", "ELITE", "NEGATE FEAR"],
    abilities: [
      "God is With Us! ACTION: Risky Success. Success: Place 1 BLESSING MARKER on self or friendly within 6\".",
      "Onward Christian Soldiers!: Friendly NEW ANTIOCH within 8\" have NEGATE FEAR.",
      "Short-Range Marksmanship: Add +1 DICE to Ranged Attacks at Short Range (except Grenades or HEAVY weapons)"
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "cleric_chewa",
        name: "Chewa",
        cost: 5,
        effects: {
          addAbilities: [
            {
              name: "Chewa",
              description: "Add +1 DICE to Melee Attacks for each friendly within 1\" of target (max +2 DICE)."
            }
          ]
        }
      }
    ],
    limit: 1
  },
  {
    name: "Holy Warrior",
    class: "Holy Warrior",
    type: "elite",
    cost: 60,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 1,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH", "ELITE", "NEGATE FEAR"],
    abilities: [
      "God is With Us! ACTION: Risky Success. Success: Place 1 BLESSING MARKER on self or friendly within 6\".",
      "Onward Christian Soldiers!: Friendly NEW ANTIOCH within 8\" have NEGATE FEAR.",
      "Blessed Psalm ACTION: Remove 1 BLESSED MARKER from self and place beside friendly. No Success Roll required.",
      "Arise and be Healed! ACTION: Risky Success. Success: Pick self or friendly within 3\". Target stands up at no movement cost, remove up to D3 BLOOD MARKERS and/or INFECTION MARKERS.",
      "Short-Range Marksmanship: Add +1 DICE to Ranged Attacks at Short Range (except Grenades or HEAVY weapons)"
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "holy_chewa",
        name: "Chewa",
        cost: 5,
        effects: {
          addAbilities: [
            {
              name: "Chewa",
              description: "Add +1 DICE to Melee Attacks for each friendly within 1\" of target (max +2 DICE)."
            }
          ]
        }
      }
    ],
    limit: 2
  },

  // ============================================================================
  // TROOPER MODELS
  // ============================================================================
  {
    name: "Yeoman",
    class: "Yeoman",
    type: "trooper",
    cost: 30,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 0,
      ranged: 0,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH"],
    abilities: [
      "Short-Range Marksmanship: Add +1 DICE to Ranged Attacks at Short Range (except Grenades or HEAVY weapons)"
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "yeoman-rifle",
        name: "Bolt-Action Rifle",
        category: "ranged",
        keywords: ["2-HANDED", "Bayonet Lug"],
        range: "24\"",
        notes: ""
      }
    ],
    upgrades: [
      {
        id: "yeoman_flanking",
        name: "Flanking",
        cost: 5,
        limit: 4,
        effects: {
          addAbilities: [
            {
              name: "Flanking",
              description: "Deploy normally OR on any board edge ≥8\" from enemies (deploy before INFILTRATORS but after other models)."
            }
          ]
        }
      }
    ]
  },
  {
    name: "Shock Trooper",
    class: "Shock Trooper",
    type: "trooper",
    cost: 45,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 0,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["NEW ANTIOCH"],
    abilities: [
      "Assault Drill: Ignore HEAVY Keyword for 1 Melee Weapon (still limited to 1 HEAVY Weapon total).",
      "Shock Charge: Roll 1 extra D6 for Charge Bonus, use single highest die."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "shock_chewa",
        name: "Chewa",
        cost: 5,
        effects: {
          addAbilities: [
            {
              name: "Chewa",
              description: "Add +1 DICE to Melee Attacks for each friendly within 1\" of target (max +2 DICE)."
            }
          ]
        }
      }
    ],
    limit: 5
  },
  {
    name: "Combat Engineer",
    class: "Combat Engineer",
    type: "trooper",
    cost: 80,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 0,
      baseArmour: -2,
      finalArmour: -2
    },
    keywords: ["NEW ANTIOCH"],
    abilities: [
      "Battlefield Demolition: Ignore HEAVY Keyword for 1 Satchel Charge (still limited to 1 HEAVY Weapon total).",
      "Defuse Mine: Risky Success to defuse when moving into mined terrain.",
      "Fortify ACTION: Risky Success → COVER until move."
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "engineer-armour",
        name: "Engineer Body Armour",
        category: "armour",
        keywords: ["NEGATE SHRAPNEL"],
        armourModifier: "-2 INJURY MODIFIER",
        notes: "Ballistic Box Armour: -1 INJURY DICE vs SHRAPNEL."
      },
      {
        id: "engineer-shovel",
        name: "Shovel",
        category: "equipment",
        keywords: [],
        notes: "COVER on Open terrain until move. Can use as 2-Handed Melee."
      }
    ],
    upgrades: [],
    limit: 2
  },
  {
    name: "Mechanized Heavy Infantry",
    class: "Mechanized Heavy Infantry",
    type: "trooper",
    cost: 85,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 0,
      ranged: 1,
      baseArmour: -2,
      finalArmour: -2
    },
    keywords: ["NEW ANTIOCH", "STRONG"],
    abilities: [],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "mhi_machine_armour",
        name: "Machine Armour",
        cost: 10,
        effects: {
          modifyStats: { baseArmour: -3, finalArmour: -3 }
        }
      }
    ],
    limit: 3
  },
  {
    name: "Combat Medic",
    class: "Combat Medic",
    type: "trooper",
    cost: 65,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 0,
      ranged: 0,
      baseArmour: -1,
      finalArmour: -1
    },
    keywords: ["NEW ANTIOCH", "NEGATE FEAR"],
    abilities: [
      "Expert Medic: Add +2 DICE to Risky Success Roll when using Treat ACTION.",
      "Finish the Fallen: Add +1 DICE to Injury Rolls for Melee Attacks vs Down targets (unless BLACK GRAIL or DEMONIC)."
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "medic-armour",
        name: "Standard Armour",
        category: "armour",
        keywords: [],
        armourModifier: "-1 INJURY MODIFIER",
        notes: ""
      },
      {
        id: "medic-gas-mask",
        name: "Gas Mask",
        category: "equipment",
        keywords: ["NEGATE GAS"],
        notes: ""
      },
      {
        id: "medic-medi-kit",
        name: "Medi-kit",
        category: "equipment",
        keywords: [],
        notes: "Treat ACTION: Risky Success to remove BLOOD MARKER or stand up Down model within 1\"."
      },
      {
        id: "medic-misericordia",
        name: "Misericordia",
        category: "melee",
        keywords: [],
        range: "",
        notes: "Despatch: This weapon has the IGNORE ARMOUR Keyword if the target is Down."
      }
    ],
    upgrades: [],
    limit: 2
  }
];

export const NEW_ANTIOCH_ALBA_ITEMS: any[] = [
  ...NEW_ANTIOCH_ITEMS.filter(item =>
    item.name !== "Musical Instrument" &&
    item.name !== "Great Sword/Axe"
  ),
  {
    name: "Bagpipes",
    category: "equipment",
    keywords: [],
    cost: 0,
    costCurrency: "ducats",
    limit: 1,
    notes: "Upgrade from Musical Instrument at no cost. Friendly models within 8\" gain NEGATE FEAR."
  },
  {
    name: "Great Sword/Axe",
    category: "melee",
    keywords: ["2-HANDED", "+1 INJURY DICE", "CRITICAL", "HEAVY"],
    range: "Melee",
    cost: 7,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Dum-Dum Bullets",
    category: "equipment",
    keywords: [],
    cost: 5,
    costCurrency: "ducats",
    limit: 3,
    notes: ""
  },
  {
    name: "Lochaber Axe",
    category: "melee",
    keywords: ["2-HANDED", "+2 INJURY DICE", "BLOCK", "CRITICAL", "CUMBERSOME", "HEAVY"],
    range: "Melee",
    cost: 20,
    costCurrency: "ducats",
    notes: ""
  }
];

export const NEW_ANTIOCH_PRUSSIA_ITEMS: any[] = [
  ...NEW_ANTIOCH_ITEMS.filter(item =>
    item.name !== "Grenade Launcher" &&
    item.name !== "Martyrdom Pills"
  ).map(item => {
    if (item.name === "Submachine Gun") {
      return { ...item, limit: 4 };
    }
    if (item.name === "Machine Gun") {
      return { ...item, limit: 1 };
    }
    if (item.name === "Automatic Shotgun" || item.name === "Automatic Pistol") {
      return { ...item, restrictions: undefined };
    }
    return item;
  }),
  {
    name: "Tank-Splitter Sword",
    category: "melee",
    keywords: ["2-HANDED", "+1 INJURY DICE", "CRITICAL", "CUMBERSOME"],
    range: "Melee",
    cost: 15,
    costCurrency: "ducats",
    limit: 2,
    notes: "Tank-Splitter: Before rolling Injury Roll vs target with Armour/Shield, set one die to 6 automatically. Not affected by Dragonslayer Patron."
  },
  {
    name: "Holy Smoke",
    category: "equipment",
    keywords: ["CONSUMABLE"],
    cost: 15,
    costCurrency: "ducats",
    limit: 1,
    restrictions: ["Trench Clerics only"],
    notes: "On deploy, consume to gain NEGATE FEAR and -1 INJURY DICE to all attacks hitting model for rest of game."
  }
];

export const NEW_ANTIOCH_ABYSSINIA_ITEMS: any[] = [
  ...NEW_ANTIOCH_ITEMS.filter(item =>
    item.name !== "Sniper Rifle" &&
    item.name !== "INFILTRATOR"
  ),
  {
    name: "Misericordia",
    category: "melee",
    keywords: ["1-HANDED"],
    range: "Melee",
    cost: 15,
    costCurrency: "ducats",
    restrictions: ["Combat Medic only"],
    limit: 2,
    notes: "Despatch: IGNORE ARMOUR vs Down target."
  },
  {
    name: "Shotel",
    category: "melee",
    keywords: ["1-HANDED", "CRITICAL"],
    range: "Melee",
    cost: 5,
    costCurrency: "ducats",
    restrictions: ["Chewa & ELITE only"],
    notes: "Bypass Shield: Ignore -INJURY DICE or -INJURY MODIFIERS from Shields."
  },
  {
    name: "Holy Water of Lalibela",
    category: "equipment",
    keywords: ["CONSUMABLE"],
    cost: 3,
    costCurrency: "ducats",
    limit: 5,
    notes: "Healing Waters: Add +1 DICE to Success Rolls for ACTIONS that remove BLOOD MARKERS and/or INFECTION MARKERS."
  },
  {
    name: "Anfarro (Warriors Crown)",
    category: "equipment",
    keywords: ["NEGATE FEAR", "Headgear"],
    cost: 10,
    costCurrency: "ducats",
    limit: 6,
    restrictions: ["Chewa & ELITE only"],
    notes: "Token of Honour: Add +1 DICE to Melee Characteristic."
  },
  {
    name: "Tabot",
    category: "equipment",
    keywords: [],
    cost: 4,
    costCurrency: "glory",
    limit: 1,
    restrictions: ["Holy Warrior only"],
    notes: "Virtuous Rewards: Place 1 BLESSING MARKER on model with Tabot each time friendly ACTION removes BLOOD/INFECTION MARKERS."
  }
];

export const TRENCH_PILGRIMS_MODELS: PremadeModelTemplate[] = [
  // ============================================================================
  // ELITE MODELS
  // ============================================================================
  {
    name: "War Prophet",
    class: "War Prophet",
    type: "elite",
    cost: 80,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 2,
      ranged: 2,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["PILGRIM", "ELITE", "LEADER"],
    abilities: [
      "Loudspeakers ACTION: Risky Success +2 DICE. Success: Move all friendly within 8\" up to 3\" towards nearest visible enemy (counts as charging if ends within 1\").",
      "Laying on of Hands ACTION: Success Roll. Success: Remove 1 BLOOD MARKER from friendly within 6\". Critical Success: Remove 3 BLOOD MARKERS instead.",
      "Memento Mori: First Out of Action result is treated as No Effect instead."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [],
    limit: 1
  },
  {
    name: "Communicant",
    class: "Communicant",
    type: "elite",
    cost: 100,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 2,
      ranged: -3,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["PILGRIM", "ELITE", "STRONG", "TOUGH"],
    abilities: [
      "Bodyguard: If friendly PILGRIM within 1\" hit, can take hit instead (except BLAST attacks).",
      "Miracle of Regeneration: On Activation (before ACTIONS), remove up to 1 BLOOD MARKER.",
      "The Communicant Cross: Counts as having Iron Capirote (Headgear) and Gas Mask at no cost, cannot be removed/lost."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [],
    limit: 1
  },
  {
    name: "Castigator",
    class: "Castigator",
    type: "elite",
    cost: 50,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 1,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["PILGRIM", "ELITE"],
    abilities: [
      "Enforced Orthodoxy ACTION: Risky Success +1 DICE. Success: All friendly Down within 8\" immediately stand up at no movement cost.",
      "Whip of God: Can target friendly within 1\" with Melee Attack. Set aside D6 each time friendly taken Out of Action. Each die adds +1 DICE to next Morale Check, then discarded."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "castigator_zealot_strength",
        name: "Zealot Strength",
        cost: 5,
        effects: {
          addKeywords: ["STRONG"]
        }
      }
    ],
    limit: 1
  },
  {
    name: "Anchorite Shrine",
    class: "Anchorite Shrine",
    type: "elite",
    cost: 140,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 2,
      ranged: 0,
      baseArmour: -3,
      finalArmour: -3
    },
    keywords: ["PILGRIM", "FEAR", "NEGATE SHRAPNEL", "STRONG", "TOUGH"],
    abilities: [
      "Broken on the Wheel: Before deployment, can permanently remove 1 Trench Pilgrim/Ecclesiastic Prisoner from Warband. Until Anchorite suffers Out of Action: Armour becomes 0, loses NEGATE SHRAPNEL and TOUGH, Down becomes Minor Hit, first Out of Action becomes No Effect (then remove all BLOOD MARKERS and this ability ends).",
      "Symphony of Slaughter: Attack once with Catherine Wheel OR Bonebreaker Mace, or twice (Catherine Wheel first, Bonebreaker Mace second with Off-Hand modifier). Both weapons are RISKY."
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "anchorite-bonebreaker",
        name: "Bonebreaker Mace",
        category: "melee",
        keywords: ["1-HANDED", "+1 INJURY DICE", "RISKY"],
        range: "Melee",
        notes: ""
      },
      {
        id: "anchorite-catherine",
        name: "Catherine Wheel",
        category: "melee",
        keywords: ["1-HANDED", "+1 DICE", "HEAVY", "RISKY"],
        range: "Melee",
        notes: "Mighty Blows: Roll 3D6, add all 3 dice. Pick 3 highest/lowest for +/-INJURY DICE."
      }
    ],
    upgrades: [],
    limit: 1
  },

  // ============================================================================
  // TROOPER MODELS
  // ============================================================================
  {
    name: "Trench Pilgrim",
    class: "Trench Pilgrim",
    type: "trooper",
    cost: 30,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 0,
      ranged: 0,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["PILGRIM"],
    abilities: [
      "Resurrection: If killed after game, can resurrect as Martyr Penitent (45 ducats). Use Martyr Penitent Profile (Melee +1), add -1 INJURY DICE to Injury Rolls. Keeps Battlekit and Zealot Strength, loses Scars/Experience/Advancements. Martyrdom Pills have no effect."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "pilgrim_zealot_strength",
        name: "Zealot Strength",
        cost: 5,
        limit: 1,
        effects: {
          addKeywords: ["STRONG"]
        }
      }
    ]
  },
  {
    name: "Martyr Penitent",
    class: "Martyr Penitent",
    type: "trooper",
    cost: 45,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 0,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["PILGRIM"],
    abilities: [
      "Add -1 INJURY DICE to Injury Rolls"
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "penitent_zealot_strength",
        name: "Zealot Strength",
        cost: 5,
        effects: {
          addKeywords: ["STRONG"]
        }
      }
    ]
  },
  {
    name: "Stigmatic Nun",
    class: "Stigmatic Nun",
    type: "trooper",
    cost: 50,
    costCurrency: "ducats",
    stats: {
      movement: 8,
      melee: 1,
      ranged: 1,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["PILGRIM"],
    abilities: [
      "Agile: Add +1 DICE to Risky Success Roll when Climbing, Jumping, Diving Charge, or taking Dash ACTION.",
      "Blessed Stigmata: On Activation (before ACTIONS), can remove up to 1 BLOOD MARKER and replace with 1 BLESSING MARKER."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [],
    limit: 4
  },
  {
    name: "Ecclesiastic Prisoner",
    class: "Ecclesiastic Prisoner",
    type: "trooper",
    cost: 20,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: -1,
      ranged: 0,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["PILGRIM"],
    abilities: [
      "Awaited: Does not count towards Out of Action for Morale Checks when taken Out by Martyrdom Device.",
      "Feeble Flailing: Can make Melee Attack without weapon. Add -1 DICE to Success Roll.",
      "Mad Dash: Add +1 DICE to Risky Success Roll when taking Dash ACTION."
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "prisoner-capirote",
        name: "Iron Capirote",
        category: "equipment",
        keywords: ["NEGATE FEAR", "NEGATE SHRAPNEL", "Headgear"],
        notes: ""
      }
    ],
    upgrades: [
      {
        id: "prisoner_martyrdom_device",
        name: "Martyrdom Device",
        cost: 35,
        limit: 4,
        effects: {
          addAbilities: [
            {
              name: "Trigger Martyrdom Device ACTION",
              description: "Detonates: Injury Roll for all within 3\" (friend/foe including self). Self: 4D6 add all dice. Others within 1\": +1 INJURY DICE. If survives game, must buy new device (35 ducats) or use without."
            }
          ]
        }
      }
    ]
  }
];

export const TRENCH_PILGRIMS_ITEMS: any[] = [
  // ============================================================================
  // RANGED WEAPONS
  // ============================================================================
  {
    name: "Automatic Pistol",
    category: "ranged",
    keywords: ["1-HANDED", "ASSAULT", "AUTOMATIC 2", "PISTOL"],
    range: "12\"/Melee",
    cost: 20,
    costCurrency: "ducats",
    limit: 2,
    notes: ""
  },
  {
    name: "Blunderbuss",
    category: "ranged",
    keywords: ["2-HANDED", "SHRAPNEL", "Shield Combo"],
    range: "10\"",
    cost: 5,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Bolt-Action Rifle",
    category: "ranged",
    keywords: ["2-HANDED", "Bayonet Lug"],
    range: "24\"",
    cost: 10,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Flamethrower",
    category: "ranged",
    keywords: ["2-HANDED", "-1 INJURY DICE", "FIRE", "FLAMETHROWER", "IGNORE ARMOUR"],
    range: "8\"",
    cost: 30,
    costCurrency: "ducats",
    limit: 2,
    notes: ""
  },
  {
    name: "Machine Gun",
    category: "ranged",
    keywords: ["2-HANDED", "AUTOMATIC 3", "HEAVY", "RELOAD"],
    range: "36\"",
    cost: 2,
    costCurrency: "glory",
    notes: ""
  },
  {
    name: "Musket",
    category: "ranged",
    keywords: ["2-HANDED", "Bayonet Lug"],
    range: "18\"",
    cost: 5,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Pistol",
    category: "ranged",
    keywords: ["1-HANDED", "PISTOL"],
    range: "12\"/Melee",
    cost: 6,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Punt Gun",
    category: "ranged",
    keywords: ["2-HANDED", "+1 DICE", "+1 INJURY DICE", "HEAVY", "SHOTGUN", "SHRAPNEL"],
    range: "18\"",
    cost: 20,
    costCurrency: "ducats",
    limit: 2,
    notes: "Overcharge: Before attack, can Overcharge if model has STRONG or is base-to-base with friendly. Gain BLAST 3\" and RELOAD. After attack: place 1 BLOOD MARKER, Activation ends."
  },
  {
    name: "Semi-Automatic Rifle",
    category: "ranged",
    keywords: ["2-HANDED", "ASSAULT", "Bayonet Lug"],
    range: "24\"",
    cost: 15,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Shotgun",
    category: "ranged",
    keywords: ["2-HANDED", "+1 DICE", "SHOTGUN", "Bayonet Lug", "Shield Combo"],
    range: "12\"",
    cost: 10,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Sniper Rifle",
    category: "ranged",
    keywords: ["2-HANDED", "+1 DICE", "CRITICAL", "RISKY"],
    range: "48\"",
    cost: 2,
    costCurrency: "glory",
    notes: "Bull's Eye: Critical Success Injury Roll gains IGNORE ARMOUR."
  },
  {
    name: "Submachine Gun",
    category: "ranged",
    keywords: ["2-HANDED", "ASSAULT", "Bayonet Lug", "Shield Combo"],
    range: "16\"",
    cost: 2,
    costCurrency: "glory",
    notes: "Quick Bursts: Can take two Shoot ACTIONS in activation."
  },

  // ============================================================================
  // MELEE WEAPONS
  // ============================================================================
  {
    name: "Anti-Tank Hammer",
    category: "melee",
    keywords: ["2-HANDED", "+1 INJURY DICE", "CRITICAL", "HEAVY", "IGNORE ARMOUR", "RISKY"],
    range: "Melee",
    cost: 35,
    costCurrency: "ducats",
    limit: 3,
    restrictions: ["ELITE only"],
    notes: "Dangerous: Place 1 BLOOD MARKER on Success/Critical."
  },
  {
    name: "Bayonet",
    category: "melee",
    keywords: ["1-HANDED", "Shield Combo"],
    range: "Melee",
    cost: 2,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Bonebreaker Mace",
    category: "melee",
    keywords: ["1-HANDED", "+1 INJURY DICE", "RISKY"],
    range: "Melee",
    cost: 0,
    costCurrency: "ducats",
    restrictions: ["Anchorite Shrine only"],
    notes: ""
  },
  {
    name: "Catherine Wheel",
    category: "melee",
    keywords: ["1-HANDED", "+1 DICE", "HEAVY", "RISKY"],
    range: "Melee",
    cost: 0,
    costCurrency: "ducats",
    restrictions: ["Anchorite Shrine only"],
    notes: "Mighty Blows: Roll 3D6, add all 3 dice. Pick 3 highest/lowest for +/-INJURY DICE."
  },
  {
    name: "Flail/Scourge",
    category: "melee",
    keywords: ["2-HANDED", "+1 DICE"],
    range: "Melee",
    cost: 5,
    costCurrency: "ducats",
    notes: "Unwieldy: +1 DICE doesn't apply Off-Hand."
  },
  {
    name: "Great Hammer/Maul",
    category: "melee",
    keywords: ["2-HANDED", "+1 INJURY MODIFIER", "HEAVY"],
    range: "Melee",
    cost: 10,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Great Sword/Axe",
    category: "melee",
    keywords: ["2-HANDED", "+1 INJURY DICE", "CRITICAL", "HEAVY"],
    range: "Melee",
    cost: 12,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Misericordia",
    category: "melee",
    keywords: ["1-HANDED"],
    range: "Melee",
    cost: 15,
    costCurrency: "ducats",
    limit: 1,
    notes: "Despatch: IGNORE ARMOUR vs Down target."
  },
  {
    name: "Polearm",
    category: "melee",
    keywords: ["2-HANDED", "BLOCK", "CUMBERSOME", "Shield Combo"],
    range: "Melee",
    cost: 7,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Sword/Axe",
    category: "melee",
    keywords: ["1-HANDED", "CRITICAL"],
    range: "Melee",
    cost: 4,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Trench Club",
    category: "melee",
    keywords: ["1-HANDED"],
    range: "Melee",
    cost: 3,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Trench Knife",
    category: "melee",
    keywords: ["1-HANDED", "-1 DICE"],
    range: "Melee",
    cost: 1,
    costCurrency: "ducats",
    notes: ""
  },

  // ============================================================================
  // GRENADES
  // ============================================================================
  {
    name: "Incendiary Grenades",
    category: "grenade",
    keywords: ["ASSAULT", "FIRE", "IGNORE COVER", "IGNORE LONG RANGE"],
    range: "8\"",
    cost: 15,
    costCurrency: "ducats",
    notes: "Liquid Fire: Critical Success Injury Roll gains IGNORE ARMOUR."
  },
  {
    name: "Molotov Cocktail",
    category: "grenade",
    keywords: ["ASSAULT", "FIRE", "IGNORE COVER", "IGNORE LONG RANGE", "-1 INJURY DICE"],
    range: "6\"",
    cost: 5,
    costCurrency: "ducats",
    notes: "Liquid Fire: Critical Success Injury Roll gains IGNORE ARMOUR."
  },
  {
    name: "Warcross",
    category: "grenade",
    keywords: ["ASSAULT", "IGNORE LONG RANGE"],
    range: "8\"",
    cost: 5,
    costCurrency: "ducats",
    notes: ""
  },

  // ============================================================================
  // SHIELDS
  // ============================================================================
  {
    name: "Holy Icon Shield",
    category: "shield",
    keywords: ["IMPERVIOUS", "Shield Combo"],
    armourModifier: "-1 INJURY MODIFIER",
    cost: 2,
    costCurrency: "glory",
    restrictions: ["ELITE only"],
    notes: ""
  },
  {
    name: "Trench Shield",
    category: "shield",
    keywords: ["Shield Combo"],
    armourModifier: "-1 INJURY MODIFIER",
    cost: 10,
    costCurrency: "ducats",
    notes: ""
  },

  // ============================================================================
  // ARMOUR
  // ============================================================================
  {
    name: "Reinforced Armour",
    category: "armour",
    keywords: [],
    armourModifier: "-2 INJURY MODIFIER",
    cost: 40,
    costCurrency: "ducats",
    restrictions: ["ELITE only"],
    notes: ""
  },
  {
    name: "Standard Armour",
    category: "armour",
    keywords: [],
    armourModifier: "-1 INJURY MODIFIER",
    cost: 15,
    costCurrency: "ducats",
    notes: ""
  },

  // ============================================================================
  // EQUIPMENT
  // ============================================================================
  {
    name: "Blessed Icon",
    category: "equipment",
    keywords: ["CONSUMABLE"],
    cost: 15,
    costCurrency: "ducats",
    notes: "Talisman: Once per game, turn a failed Risky Success into success."
  },
  {
    name: "Field Shrine",
    category: "equipment",
    keywords: [],
    cost: 2,
    costCurrency: "glory",
    notes: "Deploy terrain piece. +3 models for Morale (max +9). Can be attacked."
  },
  {
    name: "Gas Mask",
    category: "equipment",
    keywords: ["NEGATE GAS"],
    cost: 5,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Holy Relic",
    category: "equipment",
    keywords: ["BLESSED 1"],
    cost: 2,
    costCurrency: "glory",
    restrictions: ["ELITE only"],
    notes: ""
  },
  {
    name: "Incendiary Ammunition",
    category: "equipment",
    keywords: ["CONSUMABLE"],
    cost: 15,
    costCurrency: "ducats",
    limit: 1,
    notes: "Adds FIRE to one ranged weapon (no BLAST/FIRE/GAS/SHRAPNEL)."
  },
  {
    name: "Iron Capirote",
    category: "equipment",
    keywords: ["NEGATE FEAR", "NEGATE SHRAPNEL", "Headgear"],
    cost: 7,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Martyrdom Pills",
    category: "equipment",
    keywords: ["CONSUMABLE"],
    cost: 20,
    costCurrency: "ducats",
    limit: 3,
    notes: "Consume on deploy: NEGATE FEAR + -1 INJURY DICE all game."
  },
  {
    name: "Medi-kit",
    category: "equipment",
    keywords: [],
    cost: 5,
    costCurrency: "ducats",
    notes: "Treat ACTION: Risky Success to remove BLOOD MARKER or stand up Down model within 1\"."
  },
  {
    name: "Mountaineer Kit",
    category: "equipment",
    keywords: [],
    cost: 3,
    costCurrency: "ducats",
    limit: 1,
    notes: "+1 DICE to climb sheer surfaces."
  },
  {
    name: "Musical Instrument",
    category: "equipment",
    keywords: ["HELD"],
    cost: 15,
    costCurrency: "ducats",
    limit: 1,
    notes: "+1 DICE to Dash for friendly within 4\"."
  },
  {
    name: "Troop Flag",
    category: "equipment",
    keywords: ["LEADER", "HELD"],
    cost: 1,
    costCurrency: "glory",
    limit: 1,
    notes: ""
  }
];

export const TRENCH_PILGRIMS_SACRED_AFFLICTION_MODELS: PremadeModelTemplate[] = [
  // ============================================================================
  // ELITE MODELS (same as standard except special rules)
  // ============================================================================
  {
    name: "War Prophet",
    class: "War Prophet",
    type: "elite",
    cost: 80,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 2,
      ranged: 2,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["PILGRIM", "ELITE", "LEADER"],
    abilities: [
      "Loudspeakers ACTION: Risky Success +2 DICE. Success: Move all friendly within 8\" up to 3\" towards nearest visible enemy (counts as charging if ends within 1\").",
      "Laying on of Hands ACTION: Success Roll. Success: Remove 1 BLOOD MARKER from friendly within 6\". Critical Success: Remove 3 BLOOD MARKERS instead.",
      "Memento Mori: First Out of Action result is treated as No Effect instead.",
      "Punishing Millstones: Add +1 INJURY DICE to Melee Attacks vs Down targets (except Ecclesiastic Prisoners)"
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [],
    limit: 1
  },
  {
    name: "Communicant",
    class: "Communicant",
    type: "elite",
    cost: 100,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 2,
      ranged: -3,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["PILGRIM", "ELITE", "STRONG", "TOUGH"],
    abilities: [
      "Bodyguard: If friendly PILGRIM within 1\" hit, can take hit instead (except BLAST attacks).",
      "Miracle of Regeneration: On Activation (before ACTIONS), remove up to 1 BLOOD MARKER.",
      "The Communicant Cross: Counts as having Iron Capirote (Headgear) and Gas Mask at no cost, cannot be removed/lost.",
      "Punishing Millstones: Add +1 INJURY DICE to Melee Attacks vs Down targets (except Ecclesiastic Prisoners)"
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [],
    limit: 1
  },
  {
    name: "Castigator",
    class: "Castigator",
    type: "elite",
    cost: 50,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 1,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["PILGRIM", "ELITE"],
    abilities: [
      "Enforced Orthodoxy ACTION: Risky Success +1 DICE. Success: All friendly Down within 8\" immediately stand up at no movement cost.",
      "Whip of God: Can target friendly within 1\" with Melee Attack. Set aside D6 each time friendly taken Out of Action. Each die adds +1 DICE to next Morale Check, then discarded.",
      "Punishing Millstones: Add +1 INJURY DICE to Melee Attacks vs Down targets (except Ecclesiastic Prisoners)"
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "castigator_wrath_of_god",
        name: "Wrath of God",
        cost: 15,
        effects: {
          addKeywords: ["NEGATE FEAR"]
        }
      },
      {
        id: "castigator_zealot_strength",
        name: "Zealot Strength",
        cost: 5,
        effects: {
          addKeywords: ["STRONG"]
        }
      }
    ],
    limit: 1
  },
  {
    name: "Anchorite Shrine",
    class: "Anchorite Shrine",
    type: "elite",
    cost: 140,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 2,
      ranged: 0,
      baseArmour: -3,
      finalArmour: -3
    },
    keywords: ["PILGRIM", "FEAR", "NEGATE SHRAPNEL", "STRONG", "TOUGH"],
    abilities: [
      "Broken on the Wheel: Before deployment, can permanently remove 1 Trench Pilgrim/Ecclesiastic Prisoner from Warband. Until Anchorite suffers Out of Action: Armour becomes 0, loses NEGATE SHRAPNEL and TOUGH, Down becomes Minor Hit, first Out of Action becomes No Effect (then remove all BLOOD MARKERS and this ability ends).",
      "Symphony of Slaughter: Attack once with Catherine Wheel OR Bonebreaker Mace, or twice (Catherine Wheel first, Bonebreaker Mace second with Off-Hand modifier). Both weapons are RISKY.",
      "Punishing Millstones: Add +1 INJURY DICE to Melee Attacks vs Down targets (except Ecclesiastic Prisoners)"
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "anchorite-bonebreaker",
        name: "Bonebreaker Mace",
        category: "melee",
        keywords: ["1-HANDED", "+1 INJURY DICE", "RISKY"],
        range: "Melee",
        notes: ""
      },
      {
        id: "anchorite-catherine",
        name: "Catherine Wheel",
        category: "melee",
        keywords: ["1-HANDED", "+1 DICE", "HEAVY", "RISKY"],
        range: "Melee",
        notes: "Mighty Blows: Roll 3D6, add all 3 dice. Pick 3 highest/lowest for +/-INJURY DICE."
      }
    ],
    upgrades: [],
    limit: 1
  },

  // ============================================================================
  // TROOPER MODELS
  // ============================================================================
  {
    name: "Trench Pilgrim",
    class: "Trench Pilgrim",
    type: "trooper",
    cost: 30,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 0,
      ranged: 0,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["PILGRIM"],
    abilities: [
      "Resurrection: If killed after game, can resurrect as Martyr Penitent (45 ducats). Use Martyr Penitent Profile (Melee +1), add -1 INJURY DICE to Injury Rolls. Keeps Battlekit and Zealot Strength, loses Scars/Experience/Advancements. Martyrdom Pills have no effect.",
      "Punishing Millstones: Add +1 INJURY DICE to Melee Attacks vs Down targets (except Ecclesiastic Prisoners)"
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "pilgrim_zealot_strength",
        name: "Zealot Strength",
        cost: 5,
        limit: 3,
        effects: {
          addKeywords: ["STRONG"]
        }
      }
    ]
  },
  {
    name: "Martyr Penitent",
    class: "Martyr Penitent",
    type: "trooper",
    cost: 45,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 0,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["PILGRIM"],
    abilities: [
      "Add -1 INJURY DICE to Injury Rolls",
      "Punishing Millstones: Add +1 INJURY DICE to Melee Attacks vs Down targets (except Ecclesiastic Prisoners)"
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "penitent_zealot_strength",
        name: "Zealot Strength",
        cost: 5,
        limit: 3,
        effects: {
          addKeywords: ["STRONG"]
        }
      }
    ]
  },
  {
    name: "Stigmatic Nun",
    class: "Stigmatic Nun",
    type: "trooper",
    cost: 50,
    costCurrency: "ducats",
    stats: {
      movement: 8,
      melee: 1,
      ranged: 1,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["PILGRIM"],
    abilities: [
      "Agile: Add +1 DICE to Risky Success Roll when Climbing, Jumping, Diving Charge, or taking Dash ACTION.",
      "Blessed Stigmata: On Activation (before ACTIONS), can remove up to 1 BLOOD MARKER and replace with 1 BLESSING MARKER.",
      "Punishing Millstones: Add +1 INJURY DICE to Melee Attacks vs Down targets (except Ecclesiastic Prisoners)"
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [],
    limit: 4
  },
  {
    name: "Ecclesiastic Prisoner",
    class: "Ecclesiastic Prisoner",
    type: "trooper",
    cost: 20,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: -1,
      ranged: 0,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["PILGRIM"],
    abilities: [
      "Awaited: Does not count towards Out of Action for Morale Checks when taken Out by Martyrdom Device.",
      "Feeble Flailing: Can make Melee Attack without weapon. Add -1 DICE to Success Roll.",
      "Mad Dash: Add +1 DICE to Risky Success Roll when taking Dash ACTION."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "prisoner_martyrdom_device",
        name: "Martyrdom Device",
        cost: 35,
        limit: 4,
        effects: {
          addAbilities: [
            {
              name: "Trigger Martyrdom Device ACTION",
              description: "Detonates: Injury Roll for all within 3\" (friend/foe including self). Self: 4D6 add all dice. Others within 1\": +1 INJURY DICE. If survives game, must buy new device (35 ducats) or use without."
            }
          ]
        }
      }
    ]
  }
];

export const TRENCH_PILGRIMS_SAINT_METHODIUS_MODELS: PremadeModelTemplate[] = [
  // ============================================================================
  // ELITE MODELS
  // ============================================================================
  {
    name: "War Prophet",
    class: "War Prophet",
    type: "elite",
    cost: 80,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 2,
      ranged: 2,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["PILGRIM", "ELITE", "LEADER"],
    abilities: [
      "Loudspeakers ACTION: Risky Success +2 DICE. Success: Move all friendly within 8\" up to 3\" towards nearest visible enemy (counts as charging if ends within 1\").",
      "Laying on of Hands ACTION: Success Roll. Success: Remove 1 BLOOD MARKER from friendly within 6\". Critical Success: Remove 3 BLOOD MARKERS instead.",
      "Memento Mori: First Out of Action result is treated as No Effect instead."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [],
    limit: 1
  },
  {
    name: "Communicant",
    class: "Communicant",
    type: "elite",
    cost: 100,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 2,
      ranged: -3,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["PILGRIM", "ELITE", "STRONG", "TOUGH"],
    abilities: [
      "Bodyguard: If friendly PILGRIM within 1\" hit, can take hit instead (except BLAST attacks).",
      "Miracle of Regeneration: On Activation (before ACTIONS), remove up to 1 BLOOD MARKER.",
      "The Communicant Cross: Counts as having Iron Capirote (Headgear) and Gas Mask at no cost, cannot be removed/lost."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [],
  },
  {
    name: "Castigator",
    class: "Castigator",
    type: "elite",
    cost: 50,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 1,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["PILGRIM", "ELITE"],
    abilities: [
      "Enforced Orthodoxy ACTION: Risky Success +1 DICE. Success: All friendly Down within 8\" immediately stand up at no movement cost.",
      "Whip of God: Can target friendly within 1\" with Melee Attack. Set aside D6 each time friendly taken Out of Action. Each die adds +1 DICE to next Morale Check, then discarded."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "castigator_zealot_strength",
        name: "Zealot Strength",
        cost: 5,
        effects: {
          addKeywords: ["STRONG"]
        }
      }
    ],
    limit: 1
  },
  {
    name: "Anchorite Shrine",
    class: "Anchorite Shrine",
    type: "elite",
    cost: 140,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 2,
      ranged: 0,
      baseArmour: -3,
      finalArmour: -3
    },
    keywords: ["PILGRIM", "FEAR", "NEGATE SHRAPNEL", "STRONG", "TOUGH"],
    abilities: [
      "Broken on the Wheel: Before deployment, can permanently remove 1 Trench Pilgrim/Ecclesiastic Prisoner from Warband. Until Anchorite suffers Out of Action: Armour becomes 0, loses NEGATE SHRAPNEL and TOUGH, Down becomes Minor Hit, first Out of Action becomes No Effect (then remove all BLOOD MARKERS and this ability ends).",
      "Symphony of Slaughter: Attack once with Catherine Wheel OR Bonebreaker Mace, or twice (Catherine Wheel first, Bonebreaker Mace second with Off-Hand modifier). Both weapons are RISKY."
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "anchorite-bonebreaker",
        name: "Bonebreaker Mace",
        category: "melee",
        keywords: ["1-HANDED", "+1 INJURY DICE", "RISKY"],
        range: "Melee",
        notes: ""
      },
      {
        id: "anchorite-catherine",
        name: "Catherine Wheel",
        category: "melee",
        keywords: ["1-HANDED", "+1 DICE", "HEAVY", "RISKY"],
        range: "Melee",
        notes: "Mighty Blows: Roll 3D6, add all 3 dice. Pick 3 highest/lowest for +/-INJURY DICE."
      }
    ],
    upgrades: [],
    limit: 2
  },

  // ============================================================================
  // TROOPER MODELS
  // ============================================================================
  {
    name: "Trench Pilgrim",
    class: "Trench Pilgrim",
    type: "trooper",
    cost: 30,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 0,
      ranged: 0,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["PILGRIM"],
    abilities: [],
    skills: [],
    itemTemplateIds: [],
    upgrades: []
  },
  {
    name: "Stigmatic Nun",
    class: "Stigmatic Nun",
    type: "trooper",
    cost: 50,
    costCurrency: "ducats",
    stats: {
      movement: 8,
      melee: 1,
      ranged: 1,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["PILGRIM"],
    abilities: [
      "Agile: Add +1 DICE to Risky Success Roll when Climbing, Jumping, Diving Charge, or taking Dash ACTION.",
      "Blessed Stigmata: On Activation (before ACTIONS), can remove up to 1 BLOOD MARKER and replace with 1 BLESSING MARKER."
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "nun-armour",
        name: "Standard Armour",
        category: "armour",
        keywords: [],
        armourModifier: "-1 INJURY MODIFIER",
        notes: ""
      }
    ],
    upgrades: [],
    limit: 3
  },
  {
    name: "Ecclesiastic Prisoner",
    class: "Ecclesiastic Prisoner",
    type: "trooper",
    cost: 20,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: -1,
      ranged: 0,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["PILGRIM"],
    abilities: [
      "Awaited: Does not count towards Out of Action for Morale Checks when taken Out by Martyrdom Device.",
      "Feeble Flailing: Can make Melee Attack without weapon. Add -1 DICE to Success Roll.",
      "Mad Dash: Add +1 DICE to Risky Success Roll when taking Dash ACTION."
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: []
  }
];

export const TRENCH_PILGRIMS_TENTH_PLAGUE_MODELS: PremadeModelTemplate[] = [
  // ============================================================================
  // ELITE MODELS
  // ============================================================================
  {
    name: "War Prophet",
    class: "War Prophet",
    type: "elite",
    cost: 80,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 2,
      ranged: 2,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["PILGRIM", "ELITE", "LEADER"],
    abilities: [
      "Day of His Wrath ACTION: Risky Success. Success: IGNORE ARMOUR Injury Roll vs 1 enemy within 3\". Critical Success: +1 INJURY DICE and IGNORE ARMOUR Injury Roll vs 1 enemy within 3\".",
      "Memento Mori: First Out of Action result is treated as No Effect instead.",
      "Favour of the Lord: At start of each Turn, place 1 BLESSING MARKER on any model from Warband"
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [],
    limit: 1
  },
  {
    name: "Communicant",
    class: "Communicant",
    type: "elite",
    cost: 3,
    costCurrency: "glory",
    stats: {
      movement: 6,
      melee: 2,
      ranged: -3,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["PILGRIM", "ELITE", "STRONG", "TOUGH"],
    abilities: [
      "Bodyguard: If friendly PILGRIM within 1\" hit, can take hit instead (except BLAST attacks).",
      "Miracle of Regeneration: On Activation (before ACTIONS), remove up to 1 BLOOD MARKER.",
      "The Communicant Cross: Counts as having Iron Capirote (Headgear) and Gas Mask at no cost, cannot be removed/lost.",
      "Favour of the Lord: At start of each Turn, place 1 BLESSING MARKER on any model from Warband"
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [],
  },
  {
    name: "Castigator",
    class: "Castigator",
    type: "elite",
    cost: 50,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 1,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["PILGRIM", "ELITE", "TOUGH"],
    abilities: [
      "Enforced Orthodoxy ACTION: Risky Success +1 DICE. Success: All friendly Down within 8\" immediately stand up at no movement cost.",
      "Whip of God: Can target friendly within 1\" with Melee Attack. Set aside D6 each time friendly taken Out of Action. Each die adds +1 DICE to next Morale Check, then discarded.",
      "Favour of the Lord: At start of each Turn, place 1 BLESSING MARKER on any model from Warband"
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "castigator_zealot_strength",
        name: "Zealot Strength",
        cost: 5,
        effects: {
          addKeywords: ["STRONG"]
        }
      }
    ],
    limit: 1
  },

  // ============================================================================
  // TROOPER MODELS
  // ============================================================================
  {
    name: "Trench Pilgrim",
    class: "Trench Pilgrim",
    type: "trooper",
    cost: 30,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: 0,
      ranged: 0,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["PILGRIM"],
    abilities: [
      "Favour of the Lord: At start of each Turn, place 1 BLESSING MARKER on any model from Warband"
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [
      {
        id: "pilgrim_zealot_strength",
        name: "Zealot Strength",
        cost: 5,
        limit: 1,
        effects: {
          addKeywords: ["STRONG"]
        }
      }
    ]
  },
  {
    name: "Ecclesiastic Prisoner",
    class: "Ecclesiastic Prisoner",
    type: "trooper",
    cost: 20,
    costCurrency: "ducats",
    stats: {
      movement: 6,
      melee: -1,
      ranged: 0,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: ["PILGRIM"],
    abilities: [
      "Awaited: Does not count towards Out of Action for Morale Checks when taken Out by Martyrdom Device.",
      "Feeble Flailing: Can make Melee Attack without weapon. Add -1 DICE to Success Roll.",
      "Mad Dash: Add +1 DICE to Risky Success Roll when taking Dash ACTION.",
      "Favour of the Lord: At start of each Turn, place 1 BLESSING MARKER on any model from Warband"
    ],
    skills: [],
    itemTemplateIds: [],
    upgrades: [],
    limit: 2
  }
];

export const TRENCH_PILGRIMS_SACRED_AFFLICTION_ITEMS: any[] = [
  ...TRENCH_PILGRIMS_ITEMS.filter(item =>
    item.name !== "Anti-Tank Hammer" &&
    item.name !== "Holy Icon Shield" &&
    item.name !== "Punt Gun"
  ),
  {
    name: "Anti-Tank Hammer",
    category: "melee",
    keywords: ["2-HANDED", "+1 INJURY DICE", "CRITICAL", "HEAVY", "IGNORE ARMOUR", "RISKY"],
    range: "Melee",
    cost: 35,
    costCurrency: "ducats",
    notes: "Dangerous: Place 1 BLOOD MARKER on Success/Critical."
  },
  {
    name: "Holy Icon Shield",
    category: "shield",
    keywords: ["IMPERVIOUS", "Shield Combo"],
    armourModifier: "-1 INJURY MODIFIER",
    cost: 20,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Holy Icon Armour",
    category: "armour",
    keywords: ["IMPERVIOUS"],
    armourModifier: "-1 INJURY MODIFIER",
    cost: 30,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Punt Gun",
    category: "ranged",
    keywords: ["2-HANDED", "+1 DICE", "+1 INJURY DICE", "HEAVY", "SHOTGUN", "SHRAPNEL"],
    range: "18\"",
    cost: 20,
    costCurrency: "ducats",
    limit: 1,
    notes: "Overcharge: Before attack, can Overcharge if model has STRONG or is base-to-base with friendly. Gain BLAST 3\" and RELOAD. After attack: place 1 BLOOD MARKER, Activation ends."
  }
];

export const TRENCH_PILGRIMS_SAINT_METHODIUS_ITEMS: any[] = [
  ...TRENCH_PILGRIMS_ITEMS.filter(item =>
    item.name !== "Machine Gun" &&
    item.name !== "Submachine Gun" &&
    item.name !== "Catherine Wheel"
  ),
  {
    name: "Automatic Rifle",
    category: "ranged",
    keywords: ["2-HANDED", "ASSAULT", "AUTOMATIC 2", "Bayonet Lug"],
    range: "24\"",
    cost: 40,
    costCurrency: "ducats",
    limit: 1,
    notes: "Focused Fire: When this Weapon is used to make 2 Ranged Attacks, the same enemy model must be the target of both attacks."
  },
  {
    name: "Machine Gun",
    category: "ranged",
    keywords: ["2-HANDED", "AUTOMATIC 3", "HEAVY", "RELOAD"],
    range: "36\"",
    cost: 50,
    costCurrency: "ducats",
    limit: 2,
    notes: ""
  },
  {
    name: "Submachine Gun",
    category: "ranged",
    keywords: ["2-HANDED", "ASSAULT", "Bayonet Lug", "Shield Combo"],
    range: "16\"",
    cost: 30,
    costCurrency: "ducats",
    limit: 1,
    notes: "Quick Bursts: Can take two Shoot ACTIONS in activation."
  },
  {
    name: "Anti-Materiel Rifle",
    category: "ranged",
    keywords: ["1-HANDED", "+1 INJURY DICE", "CRITICAL", "HEAVY", "IGNORE ARMOUR"],
    range: "36\"",
    cost: 40,
    costCurrency: "ducats",
    restrictions: ["Anchorite Shrine only"],
    notes: ""
  },
  {
    name: "Autocannon",
    category: "ranged",
    keywords: ["1-HANDED", "+1 INJURY DICE", "AUTOMATIC 3", "HEAVY", "AUTOMATIC 5", "HEAVY", "RELOAD", "RISKY"],
    range: "48\"",
    cost: 55,
    costCurrency: "ducats",
    restrictions: ["Anchorite Shrine only"],
    notes: "Full Auto: Each attack, choose Bursts or Full Auto profile."
  },
  {
    name: "Gas Censer",
    category: "ranged",
    keywords: ["1-HANDED", "GAS", "IGNORE ARMOUR", "RELOAD"],
    range: "6\"",
    cost: 50,
    costCurrency: "ducats",
    restrictions: ["Anchorite Shrine only"],
    notes: "Cloud of Gas: Instead of Ranged Attack, all models within 6\" are hit (friend/foe except attacker, regardless of LoS)."
  },
  {
    name: "Heavy Flamethrower",
    category: "ranged",
    keywords: ["1-HANDED", "-1 INJURY DICE", "AUTOMATIC 2", "FIRE", "FLAMETHROWER", "HEAVY", "IGNORE ARMOUR"],
    range: "10\"",
    cost: 45,
    costCurrency: "ducats",
    restrictions: ["Anchorite Shrine only"],
    notes: ""
  },
  {
    name: "Punt Gun",
    category: "ranged",
    keywords: ["1-HANDED", "+1 DICE", "+1 INJURY DICE", "HEAVY", "SHOTGUN", "SHRAPNEL"],
    range: "18\"",
    cost: 10,
    costCurrency: "ducats",
    restrictions: ["Anchorite Shrine only"],
    notes: "Overcharge: Before attack, can Overcharge if model has STRONG or is base-to-base with friendly. Gain BLAST 3\" and RELOAD. After attack: place 1 BLOOD MARKER, Activation ends."
  },
  {
    name: "Trench Mortar",
    category: "ranged",
    keywords: ["1-HANDED", "+1 INJURY DICE", "BLAST 3\"", "FIRE", "HEAVY", "IGNORE COVER", "SCATTER"],
    range: "6\"-36\"",
    cost: 40,
    costCurrency: "ducats",
    restrictions: ["Anchorite Shrine only"],
    notes: "High Trajectory: Target cannot be within 6\" of attacker."
  },
  {
    name: "Gas Filters",
    category: "equipment",
    keywords: ["NEGATE GAS"],
    cost: 5,
    costCurrency: "ducats",
    restrictions: ["Anchorite Shrine only"],
    notes: ""
  },
  {
    name: "Grand Anchorite",
    category: "equipment",
    keywords: [],
    cost: 15,
    costCurrency: "ducats",
    limit: 1,
    restrictions: ["Anchorite Shrine only"],
    notes: "Impossible to Stop: Enemies cannot Melee Attack when this retreats. Can use normal move/charge if starts within 1\" of enemies."
  },
  {
    name: "Hallowed Anchorite",
    category: "equipment",
    keywords: [],
    cost: 10,
    costCurrency: "ducats",
    limit: 1,
    restrictions: ["Anchorite Shrine only"],
    notes: "Manifold Blessings: Can be promoted to ELITE status."
  },
  {
    name: "Holy Diesel Engine",
    category: "equipment",
    keywords: [],
    cost: 10,
    costCurrency: "ducats",
    restrictions: ["Anchorite Shrine only"],
    notes: "Advanced Design: When taking Dash ACTION, can add +2 DICE to Risky Roll. If do, place 1 BLOOD MARKER."
  },
  {
    name: "Piston Legs",
    category: "equipment",
    keywords: [],
    cost: 10,
    costCurrency: "ducats",
    restrictions: ["Anchorite Shrine only"],
    notes: "Grind to Dust ACTION: Melee Attack targeting Down enemy within 1\" (≤32mm base)."
  },
  {
    name: "Sacred Geometry",
    category: "equipment",
    keywords: [],
    cost: 10,
    costCurrency: "ducats",
    limit: 1,
    restrictions: ["Anchorite Shrine only"],
    notes: "Divine Accuracy: Add +1 DICE to Ranged Characteristic."
  },
  {
    name: "Wrathful Cherub Face",
    category: "equipment",
    keywords: [],
    cost: 20,
    costCurrency: "ducats",
    restrictions: ["Anchorite Shrine only"],
    notes: "Cower Before The Lord: Enemy without FEAR starting Activation within 1\" must Retreat. Cannot take RISKY ACTIONS while within 1\"."
  }
];

export const TRENCH_PILGRIMS_TENTH_PLAGUE_ITEMS: any[] = [
  ...TRENCH_PILGRIMS_ITEMS,
  {
    name: "Sacrificial Lamb",
    category: "equipment",
    keywords: ["CONSUMABLE"],
    cost: 5,
    costCurrency: "ducats",
    notes: "On deploy, can sacrifice and anoint. First BLOOD MARKER placed is ignored. Ecclesiastic Prisoners cannot have."
  }
];

export interface FactionVariant {
  name: string;
  description?: string;
  models: PremadeModelTemplate[];
  items: any[];
}

export interface FactionData {
  name: string;
  description?: string;
  variants: Record<string, FactionVariant>;
}

// ============================================================================
// MERCENARIES - MODELS
// ============================================================================

export const MERCENARY_MODELS: PremadeModelTemplate[] = [
  {
    name: "Mamluk Faris",
    class: "Mamluk Faris",
    type: "trooper",
    cost: 4,
    costCurrency: "glory",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 1,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: [
      "FIRETEAM",
      "IGNORE OFF-HAND WEAPON"
    ],
    abilities: [
      "Automaton Destrier: If INFILTRATOR allowed, can deploy on any table edge and 8\" from enemy models",
      "Martial Prowess: They ignore HEAVY on Greatsword; their Jezzail has ASSAULT and Shield Combo",
      "Sword Brethren: Can form an additional FIRETEAM with any ELITE model, gains Concentrated Fire if in NA"
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "mamluk-jezzail",
        name: "Jezzail",
        category: "ranged",
        keywords: [],
        range: "18\"",
        notes: ""
      },
      {
        id: "mamluk-ammo",
        name: "Alchemical Ammunition",
        category: "equipment",
        keywords: [],
        notes: "Guiding Path: +1 DICE to Ranged Attacks with Jezzail"
      },
      {
        id: "mamluk-armour",
        name: "Reinforced Armour",
        category: "armour",
        keywords: [],
        armourModifier: "-2 INJURY MODIFIER",
        notes: ""
      },
      {
        id: "mamluk-helmet",
        name: "Combat Helmet",
        category: "equipment",
        keywords: ["NEGATE SHRAPNEL"],
        notes: ""
      },
    ],
    upgrades: [],
    limit: 1
  },
  {
    name: "Combat Biologist",
    class: "Combat Biologist",
    type: "trooper",
    cost: 3,
    costCurrency: "glory",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 0,
      baseArmour: 1,
      finalArmour: 1
    },
    keywords: [
      "NEGATE FEAR"
    ],
    abilities: [
      "Battlefield Vivisection: Get 1 extra Glorious Deed - 3 or more enemy models OoA in Melee of Biologist",
      "Prize Specimens: When taking DEMONIC or BLACK GRAIL OoA with Melee -> 1 BLESSING MARKER"
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "biologist-vivisector",
        name: "Vivisector",
        category: "melee",
        keywords: [
          "BLOCK",
          "CUMBERSOME",
          "2-HANDED"
        ],
        notes: "Pain Amplifier: Lower the roll needed for a Critical Success for this weapon by 1 for each BLOOD MARKER the target has (so an enemy with 1 BLOOD MARKER will suffer a Critical Success on a roll of 11+).\n\nInflict Pain: (Ignore for 40mm+ or ARTIFICIAL target models) When this weapon makes a Critical Success roll, force the target to retreat D3\" directly away and if leaves melee, provoke attack of opportunity. If that triggers another critical, it add +D3\" to retreat, but no attack of opportunity."
      },
      {
        id: "biologist-gas-grenades",
        name: "Gas Grenades",
        category: "grenade",
        keywords: [
          "-1 INJURY DICE",
          "ASSAULT",
          "BLAST 3\"",
          "GAS",
          "IGNORE ARMOUR",
          "IGNORE COVER",
          "IGNORE LONG RANGE"
        ],
        range: "8\"",
        notes: ""
      },
      {
        id: "biologist-gas-mask",
        name: "Gas Mask",
        category: "equipment",
        keywords: ["NEGATE GAS"],
        notes: ""
      }
    ],
    upgrades: [],
    limit: 1
  },
  {
    name: "Communicant Anti-Tank Hunter",
    class: "Communicant Anti-Tank Hunter",
    type: "trooper",
    cost: 5,
    costCurrency: "glory",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 1,
      baseArmour: 1,
      finalArmour: 1
    },
    keywords: [
      "TOUGH",
      "STRONG"
    ],
    abilities: [
      "Iron Fists: Can make 2 Melee attacks without having a melee weapon"
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "hunter-anti-material",
        name: "Anti-Materiel Rifle",
        category: "ranged",
        keywords: [
          "+1 INJURY DICE",
          "CRITICAL",
          "HEAVY",
          "IGNORE ARMOUR"
        ],
        range: "36\"",
        notes: ""
      },
      {
        id: "hunter-helmet",
        name: "Combat Helmet",
        category: "equipment",
        keywords: ["NEGATE SHRAPNEL"],
        notes: ""
      }
    ],
    upgrades: [],
    limit: 1
  },
  {
    name: "Goetic Warlock",
    class: "Goetic Warlock",
    type: "trooper",
    cost: 4,
    costCurrency: "glory",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 1,
      baseArmour: 2,
      finalArmour: 2
    },
    keywords: [
      "ARTIFICIAL",
      "DEMONIC",
      "FEAR"
    ],
    abilities: [
      "Barbed Embrace: Enemy models cannot retreat from melee",
      "Iron-Clawed Hands: Can attack twice in melee, with CRITICAL",
      "Goetic Gaze ACTION: 24\" range and line of sight -> success= 1 BLOOD MARKER; critical success= 2 BLOOD MARKERS",
      "Goetic Portal ACTION: RISKY with +1 DICE, if successful= redeploy within 6\" without triggereing opportunity attacks and can deploy into melee. If enemy model 32mm or smaller is in melee, can take with them"
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "warlock-claws",
        name: "Iron-Clawed Hands",
        category: "melee",
        keywords: ["CRITICAL"],
        range: "",
        notes: "Natural weapons - can attack twice in melee"
      }
    ],
    upgrades: [],
    limit: 1
  },
  {
    name: "Mendelist Ammo Monk",
    class: "Mendelist Ammo Monk",
    type: "trooper",
    cost: 2,
    costCurrency: "glory",
    stats: {
      movement: 6,
      melee: -1,
      ranged: -1,
      baseArmour: 0,
      finalArmour: 0
    },
    keywords: [],
    abilities: [
      "Ammunition Sacrament ACTION: RISKY, on success choose 1 of 3 effects for ranged attacks on model within 1\"",
      "*Bullet of Guided Path: +1 DICE to hit rolls\n*Cartridge of his Wrath Sacrament: Add BLAST 2\" and SHRAPNEL Keyword to weapons (except FLAMTHROWER/BLAST) \n*Echo of his Word Sacrament: Add +1 INJURY DICE for ranged attacks",
      "Faithful Followers: Ammo Monk can be activated with other friendly within 1\" and can do ACTIONS in any order. Forceful ends to activation don't end the activation of the other model."
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "monk-gas-mask",
        name: "Gas Mask",
        category: "equipment",
        keywords: ["NEGATE GAS"],
        notes: ""
      }
    ],
    upgrades: [],
    limit: 1
  },
  {
    name: "Observer",
    class: "Observer",
    type: "trooper",
    cost: 3,
    costCurrency: "glory",
    stats: {
      movement: 8,
      melee: 1,
      ranged: 2,
      baseArmour: -1,
      finalArmour: -1
    },
    keywords: [],
    abilities: [
      "Eye of God: Can re-roll any success rolls; if any dice show a 1 -> Observer down and +1 BLOOD MARKER",
      "Lightning Speed: Can attack twice with Polearm",
      "Temporal Fugue: Add -1 DICE for enemy attacks against them",
      "Voice of God ACTION: RISKY, if successful= Observer activation ends and enemy model immediately activates"
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "observer-medi-kit",
        name: "Medi-kit",
        category: "equipment",
        keywords: [],
        notes: "Treat ACTION: Risky Success to remove BLOOD MARKER or stand up Down model within 1\"."
      },
      {
        id: "observer-helmet",
        name: "Combat Helmet",
        category: "equipment",
        keywords: ["NEGATE SHRAPNEL"],
        notes: ""
      },
      {
        id: "observer-gas-mask",
        name: "Gas Mask",
        category: "equipment",
        keywords: ["NEGATE GAS"],
        notes: ""
      },
      {
        id: "observer-polearm",
        name: "Polearm",
        category: "melee",
        keywords: ["BLOCK", "CUMBERSOME", "SHIELD COMBO"],
        range: "",
        notes: "2-Handed"
      }
    ],
    upgrades: [],
    limit: 1
  },
  {
    name: "Scripture Guardian",
    class: "Scripture Guardian",
    type: "trooper",
    cost: 7,
    costCurrency: "glory",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 0,
      baseArmour: 2,
      finalArmour: 2
    },
    keywords: [
      "GOLEM"
    ],
    abilities: [
      "Slow: 3\" movement when Dashing",
      "Vengeful Scripture ACTION: 18\" Range and LOS, within 9\" +2 DICE, outside +1 DICE; if successful = Injury roll with +1 INJURY modifier per BLOOD MARKER (which still count when spent); critical success = IGNORE ARMOR and +1 INJURY DICE extra"
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "guardian-helmet",
        name: "Combat Helmet",
        category: "equipment",
        keywords: ["NEGATE SHRAPNEL"],
        notes: ""
      }
    ],
    upgrades: [],
    limit: 1
  },
  {
    name: "Sin Eater",
    class: "Sin Eater",
    type: "trooper",
    cost: 6,
    costCurrency: "glory",
    stats: {
      movement: 6,
      melee: 2,
      ranged: 0,
      baseArmour: 2,
      finalArmour: 2
    },
    keywords: [
      "FEAR",
      "DEMONIC",
      "TOUGH",
      "STRONG"
    ],
    abilities: [
      "Devour the Guilty ACTION: pick 1 model within 1\" (friend or foe), RISKY if enemy, Normal and +1 DICE for friendly, on success= Model is devoured",
      "*Place model outside of battlefield with any markers, max 1 at a time (model inside is down for morale)\n*Devoured model can activate and only make fight ACTION, add -3 DICE and ignore any ability or weapon effects\n*When Sin eater is OoA, place Devoured model within 1\" and Down; if impossible take OoA\n*If Devoured model inside at end of game-> OoA\n*Add +1 BLOOD MARKER next to Devoured Model each time Sin Eater activates. On 6th BLOOD MARKER immediately OoA\n*Purge ACTION: +4 DICE, if successful= place model within 1\" and down"
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "sin-eater-maul",
        name: "Tenderiser Maul",
        category: "melee",
        keywords: [
          "+1 INJURY MODIFIER",
          "HEAVY",
          "2-HANDED"
        ],
        notes: "Swinging Blow: When Sin Eater makes a fight ACTION, may make 1 melee attack against each model in melee, one after the other"
      },
      {
        id: "sin-eater-helmet",
        name: "Combat Helmet",
        category: "equipment",
        keywords: ["NEGATE SHRAPNEL"],
        notes: ""
      }
    ],
    upgrades: [],
    limit: 1
  },
  {
    name: "Sister of Saint Cosmas",
    class: "Sister of Saint Cosmas",
    type: "trooper",
    cost: 2,
    costCurrency: "glory",
    stats: {
      movement: 6,
      melee: 0,
      ranged: 0,
      baseArmour: -1,
      finalArmour: -1
    },
    keywords: [
      "NEGATE FEAR"
    ],
    abilities: [
      "Expert Medic: +2 DICE for Treat ACTIONS",
      "Finish the Fallen: Add +1 DICE to Injury Rolls against enemies who are Down (except DEMONIC or BLACK GRAIL)"
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "sister-medi-kit",
        name: "Medi-kit",
        category: "equipment",
        keywords: [],
        notes: "Treat ACTION: Risky Success to remove BLOOD MARKER or stand up Down model within 1\"."
      },
      {
        id: "sister-misericordia",
        name: "Misericordia",
        category: "melee",
        keywords: [],
        range: "",
        notes: "Despatch: This weapon has the IGNORE ARMOUR Keyword if the target is Down."
      }
    ],
    upgrades: [],
    limit: 1
  },
  {
    name: "Witchburner",
    class: "Witchburner",
    type: "trooper",
    cost: 5,
    costCurrency: "glory",
    stats: {
      movement: 6,
      melee: 1,
      ranged: 0,
      baseArmour: 2,
      finalArmour: 2
    },
    keywords: [
      "ELITE",
      "NEGATE FEAR"
    ],
    abilities: [
      "Dignified Conduct: Cannot make Dash ACTIONS",
      "Divine Judgment ACTION: 24\" Range (LoS not needed), RISKY, on success= 1 BLOOD MARKER next to enemy; 2 BLOOD MARKERS if enemy is HERETIC, BLACK GRAIL or DEMONIC"
    ],
    skills: [],
    itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "witchburner-gavel",
        name: "Gavel of Justice",
        category: "melee",
        keywords: [
          "CRITICAL",
          "1-HANDED"
        ],
        notes: "Wrath of God: After resolving injury= +1 BLOOD MARKER next to enemy; +2 BLOOD MARKERS if enemy is HERETIC, BLACK GRAIL or DEMONIC"
      },
      {
        id: "witchburner-helmet",
        name: "Combat Helmet",
        category: "equipment",
        keywords: ["NEGATE SHRAPNEL"],
        notes: ""
      }
    ],
    upgrades: [],
    limit: 1
  }
];

// ============================================================================
// MERCENARIES - ITEMS
// ============================================================================

export const MERCENARY_ITEMS: PremadeItemTemplate[] = [
  {
    name: "Bayonet",
    category: "melee",
    keywords: [
      "CUMBERSOME"
    ],
    range: "",
    armourModifier: "",
    notes: "Shield Combo",
    cost: 2,
    costCurrency: "ducats"
  },
  {
    name: "Great Hammer/Maul",
    category: "melee",
    keywords: [
      "+1 INJURY MODIFIER",
      "HEAVY"
    ],
    range: "",
    armourModifier: "",
    notes: "",
    cost: 10,
    costCurrency: "ducats"
  },
  {
    name: "Great Sword/Axe",
    category: "melee",
    keywords: [
      "+1 INJURY DICE",
      "CRITICAL",
      "HEAVY"
    ],
    range: "",
    armourModifier: "",
    notes: "",
    cost: 12,
    costCurrency: "ducats"
  },
  {
    name: "Polearm",
    category: "melee",
    keywords: [
      "BLOCK",
      "CUMBERSOME"
    ],
    range: "",
    armourModifier: "",
    notes: "Shield Combo",
    cost: 7,
    costCurrency: "ducats"
  },
  {
    name: "Sword/Axe",
    category: "melee",
    keywords: [
      "CRITICAL"
    ],
    range: "",
    armourModifier: "",
    notes: "",
    cost: 4,
    costCurrency: "ducats"
  },
  {
    name: "Trench Club",
    category: "melee",
    keywords: [],
    range: "",
    armourModifier: "",
    notes: "",
    cost: 3,
    costCurrency: "ducats"
  },
  {
    name: "Trench Knife",
    category: "melee",
    keywords: [
      "-1 DICE"
    ],
    range: "",
    armourModifier: "",
    notes: "",
    cost: 1,
    costCurrency: "ducats"
  },
  {
    name: "Flamethrower",
    category: "ranged",
    keywords: [
      "-1 INJURY DICE",
      "FIRE",
      "FLAMETHROWER",
      "IGNORE ARMOUR"
    ],
    range: "8\"",
    cost: 30,
    costCurrency: "ducats",
    notes: "",
    limit: 3
  },
  {
    name: "Jezzail",
    category: "ranged",
    keywords: [],
    range: "18\"",
    cost: 7,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Machine Gun",
    category: "ranged",
    keywords: [
      "AUTOMATIC 3",
      "HEAVY",
      "RELOAD"
    ],
    range: "36\"",
    cost: 50,
    costCurrency: "ducats",
    notes: "Limit: 1",
    limit: 1
  },
  {
    name: "Musket",
    category: "ranged",
    keywords: [
      "-1 INJURY DICE"
    ],
    range: "18\"",
    cost: 5,
    costCurrency: "ducats",
    notes: "Bayonet Lug"
  },
  {
    name: "Pistol",
    category: "ranged",
    keywords: [
      "PISTOL"
    ],
    range: "12\"/Melee",
    cost: 6,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Shotgun",
    category: "ranged",
    keywords: [
      "+1 DICE",
      "SHOTGUN"
    ],
    range: "12\"",
    cost: 10,
    costCurrency: "ducats",
    notes: "Bayonet Lug, Shield Combo"
  },
  {
    name: "Reinforced Armour",
    category: "armour",
    keywords: [],
    range: "",
    armourModifier: "-2 INJURY MODIFIER",
    notes: "ELITE & Janissaries only",
    cost: 40,
    costCurrency: "ducats"
  },
  {
    name: "Standard Armour",
    category: "armour",
    keywords: [],
    range: "",
    armourModifier: "-1 INJURY MODIFIER",
    notes: "",
    cost: 15,
    costCurrency: "ducats"
  },
  {
    name: "Trench Shield",
    category: "shield",
    keywords: [],
    range: "",
    armourModifier: "-1 INJURY MODIFIER",
    notes: "Shield Combo",
    cost: 10,
    costCurrency: "ducats"
  },
  {
    name: "Frag Grenades",
    category: "grenade",
    keywords: [
      "ASSAULT",
      "BLAST 2\"",
      "IGNORE COVER",
      "IGNORE LONG RANGE",
      "SHRAPNEL"
    ],
    range: "8\"",
    armourModifier: "",
    notes: "",
    cost: 7,
    costCurrency: "ducats"
  },
  {
    name: "Alchemical Ammunition",
    category: "equipment",
    keywords: [],
    range: "",
    armourModifier: "",
    notes: "Models with Jezzail, Siege Jezzail, Alaybozan or Halberd-Gun only\nGuiding Path: +1 DICE to Ranged Attacks with those weapons.",
    cost: 3,
    costCurrency: "ducats"
  },
  {
    name: "Binoculars",
    category: "equipment",
    keywords: [],
    range: "",
    armourModifier: "",
    notes: "ELITE only\nBinoculars: INFILTRATOR cannot deploy within 16\" unless in own zone.",
    cost: 10,
    costCurrency: "ducats"
  },
  {
    name: "Combat Helmet",
    category: "equipment",
    keywords: [
      "NEGATE SHRAPNEL"
    ],
    range: "",
    armourModifier: "",
    notes: "",
    cost: 5,
    costCurrency: "ducats"
  },
  {
    name: "Gas Mask",
    category: "equipment",
    keywords: [
      "NEGATE GAS"
    ],
    range: "",
    armourModifier: "",
    notes: "",
    cost: 5,
    costCurrency: "ducats"
  },
  {
    name: "Holy Relic",
    category: "equipment",
    keywords: [
      "BLESSED 1"
    ],
    range: "",
    armourModifier: "",
    notes: "ELITE only",
    cost: 2,
    costCurrency: "glory"
  },
  {
    name: "Medi-kit",
    category: "equipment",
    keywords: [],
    range: "",
    armourModifier: "",
    notes: "Treat ACTION: Risky Success to remove BLOOD MARKER or stand up Down model within 1\".",
    cost: 5,
    costCurrency: "ducats"
  },
  {
    name: "Mountaineer Kit",
    category: "equipment",
    keywords: [],
    range: "",
    armourModifier: "",
    notes: "Limit: 2\n+1 DICE to climb sheer surfaces.",
    cost: 3,
    costCurrency: "ducats",
    limit: 2
  },
  {
    name: "Musical Instrument",
    category: "equipment",
    keywords: [
      "HELD"
    ],
    range: "",
    armourModifier: "",
    notes: "Limit: 1\n+1 DICE to Dash for friendly within 4\".",
    cost: 15,
    costCurrency: "ducats",
    limit: 1
  },
  {
    name: "Shovel",
    category: "equipment",
    keywords: [],
    range: "",
    armourModifier: "",
    notes: "COVER on Open terrain until move. Can use as 2-Handed Melee.",
    cost: 5,
    costCurrency: "ducats"
  },
  {
    name: "Troop Flag",
    category: "equipment",
    keywords: [
      "LEADER",
      "HELD"
    ],
    range: "",
    armourModifier: "",
    notes: "Limit: 1",
    cost: 1,
    costCurrency: "glory",
    limit: 1
  },
  {
    name: "Wind Amulet",
    category: "equipment",
    keywords: [],
    range: "",
    armourModifier: "",
    notes: "Limit: 2\nGusts of Wind: Once per game, +3\" Movement for activation.",
    cost: 10,
    costCurrency: "ducats",
    limit: 2
  },
  {
    name: "Sniper Rifle",
    category: "ranged",
    keywords: [
      "+1 DICE",
      "CRITICAL",
      "RISKY"
    ],
    range: "48\"",
    cost: 35,
    costCurrency: "ducats",
    notes: "ELITE & Pashtun Marksmen only, Limit: 2\nBull's Eye: Critical Success Injury Roll gains IGNORE ARMOUR.",
    limit: 2
  },
  {
    name: "Gas Grenades",
    category: "grenade",
    keywords: [
      "-1 INJURY DICE",
      "ASSAULT",
      "BLAST 3\"",
      "GAS",
      "IGNORE ARMOUR",
      "IGNORE COVER",
      "IGNORE LONG RANGE"
    ],
    range: "8\"",
    armourModifier: "",
    notes: "",
    cost: 10,
    costCurrency: "ducats"
  },
  {
    name: "Vivisector",
    category: "melee",
    keywords: [
      "BLOCK",
      "CUMBERSOME",
      "2-HANDED"
    ],
    range: "",
    armourModifier: "",
    notes: "Combat Biologist only\n\n*Pain Amplifier: Lower the roll needed for a Critical Success for this weapon by 1 for each BLOOD MARKER the target has (so an enemy with 1 BLOOD MARKER will suffer a Critical Success on a roll of 11+).\n\n*Inflict Pain: (Ignore for 40mm+ or ARTIFICIAL target models) When this weapon makes a Critical Success roll, force the target to retreat D3\" directly away and if leaves melee, provoke attack of opportunity. If that triggers another critical, it add +D3\" to retreat, but no attack of opportunity.",
    cost: 0,
    costCurrency: "ducats"
  },
  {
    name: "Anti-Materiel Rifle",
    category: "ranged",
    keywords: [
      "+1 INJURY DICE",
      "CRITICAL",
      "HEAVY",
      "IGNORE ARMOUR"
    ],
    range: "36\"",
    cost: 0,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Tenderiser Maul",
    category: "melee",
    keywords: [
      "+1 INJURY MODIFIER",
      "HEAVY",
      "2-HANDED"
    ],
    range: "",
    armourModifier: "",
    notes: "Sin Eater only\n\nSwinging Blow: When Sin Eater makes a fight ACTION, may make 1 melee attack against each model in melee, one after the other",
    cost: 0,
    costCurrency: "ducats"
  },
  {
    name: "Misericordia",
    category: "melee",
    keywords: [],
    range: "",
    armourModifier: "",
    notes: "Despatch: This weapon has the IGNORE ARMOUR Keyword if the target is Down.",
    cost: 0,
    costCurrency: "ducats"
  },
  {
    name: "Gavel of Justice",
    category: "melee",
    keywords: [
      "CRITICAL",
      "1-HANDED"
    ],
    range: "",
    armourModifier: "",
    notes: "Witchburner only\n\nWrath of God: After resolving injury= +1 BLOOD MARKER next to enemy; +2 BLOOD MARKERS if enemy is HERETIC, BLACK GRAIL or DEMONIC",
    cost: 0,
    costCurrency: "ducats"
  }
];

export const FACTIONS: Record<string, FactionData> = {
  "Trench Pilgrims": {
    name: "Trench Pilgrims",
    description: "The fanatical followers of the Supreme Pontiff, seeking martyrdom and divine favor in the trenches of war",
    variants: {
      "Standard": {
        name: "Standard Trench Pilgrims",
        description: "The classic Trench Pilgrims army list with their holy relics and martyr priests",
        models: TRENCH_PILGRIMS_MODELS,
        items: TRENCH_PILGRIMS_ITEMS
      },
      "Procession of the Sacred Affliction": {
        name: "Procession of the Sacred Affliction",
        description: "A fanatical sect that embraces suffering and divine wrath, with enhanced punishment abilities",
        models: TRENCH_PILGRIMS_SACRED_AFFLICTION_MODELS,
        items: TRENCH_PILGRIMS_SACRED_AFFLICTION_ITEMS
      },
      "War Pilgrimage of Saint Methodius": {
        name: "War Pilgrimage of Saint Methodius",
        description: "Anchorite Shrine-focused warband with advanced mechanical weaponry and divine blessings",
        models: TRENCH_PILGRIMS_SAINT_METHODIUS_MODELS,
        items: TRENCH_PILGRIMS_SAINT_METHODIUS_ITEMS
      },
      "Cavalcade of the Tenth Plague": {
        name: "Cavalcade of the Tenth Plague",
        description: "Blessing-focused warband that brings divine favor and wrath to the battlefield",
        models: TRENCH_PILGRIMS_TENTH_PLAGUE_MODELS,
        items: TRENCH_PILGRIMS_TENTH_PLAGUE_ITEMS
      }
    }
  },

  "Iron Sultanate": {
    name: "Iron Sultanate",
    description: "The mechanized empire of the Iron Sultanate",
    variants: {
      "Standard": {
        name: "Standard Iron Sultanate",
        description: "The classic Iron Sultanate army list",
        models: [
          {
            name: "Yüzbaşı",
            class: "Yüzbaşı",
            type: "elite",
            cost: 70,
            costCurrency: "ducats",
            stats: {
              movement: 6,
              melee: 2,
              ranged: 2,
              baseArmour: 0,
              finalArmour: 0
            },
            keywords: ["SULTANATE", "ELITE", "LEADER", "NEGATE FEAR", "TOUGH"],
            abilities: [
              "Janissary Veteran: +10 ducats for STRONG and Counter-Charge (+1 DICE Melee after Charge).",
              "Mubarizun: +1 INJURY DICE vs TOUGH targets."
            ],
            skills: [],
            itemTemplateIds: [],
            upgrades: [
              {
                id: "yuzbasi_veteran_leader",
                name: "Veteran Leader",
                cost: 15,
                effects: {
                  addKeywords: ["VETERAN"]
                }
              },
              {
                id: "yuzbasi_commander",
                name: "Field Commander",
                cost: 25,
                effects: {
                  modifyStats: { ranged: 1 }
                }
              }
            ]
          },
          {
            name: "Jabirean Alchemist",
            class: "Jabirean Alchemist",
            type: "elite",
            cost: 55,
            costCurrency: "ducats",
            stats: {
              movement: 6,
              melee: 1,
              ranged: 2,
              baseArmour: 0,
              finalArmour: 0
            },
            keywords: ["SULTANATE", "ELITE"],
            abilities: [
              "Mastery of the Elements: Choose FIRE/GAS/SHRAPNEL for all weapons on deploy.",
              "Elemental Change ACTION: Risky Success to change element."
            ],
            skills: [],
            itemTemplateIds: [],
            upgrades: []
          },
          {
            name: "Sultanate Assassin",
            class: "Sultanate Assassin",
            type: "elite",
            cost: 85,
            costCurrency: "ducats",
            stats: {
              movement: 6,
              melee: 2,
              ranged: 1,
              baseArmour: 0,
              finalArmour: 0
            },
            keywords: ["SULTANATE", "ELITE", "INFILTRATOR"],
            abilities: [
              "Temporal Assassin: Charge two enemies, attack both once each.",
              "Time Slip: On failed enemy attack, redeploy within 6\" (>1\" from enemies)."
            ],
            skills: [],
            itemTemplateIds: [],
            upgrades: []
          },
          {
            name: "Azeb",
            class: "Azeb",
            type: "trooper",
            cost: 25,
            costCurrency: "ducats",
            stats: {
              movement: 6,
              melee: -1,
              ranged: 0,
              baseArmour: 0,
              finalArmour: 0
            },
            keywords: ["SULTANATE"],
            abilities: ["Light Skirmishers: +5 ducats each for SKIRMISHER."],
            skills: [],
            itemTemplateIds: [],
            upgrades: []
          },
          {
            name: "Sultanate Sapper",
            class: "Sultanate Sapper",
            type: "trooper",
            cost: 50,
            costCurrency: "ducats",
            stats: {
              movement: 6,
              melee: 0,
              ranged: 1,
              baseArmour: 0,
              finalArmour: 0
            },
            keywords: ["SULTANATE"],
            abilities: [
              "Set Mine ACTION: Success +2 DICE → terrain Mined.",
              "Defuse Mine: Risky Success to defuse.",
              "Fortify ACTION: Risky Success → COVER until move.",
              "Forward Positions: Deploy up to 6\" forward in contact with terrain ≥½\" tall."
            ],
            skills: [],
            itemTemplateIds: [],
    inbuiltEquipment: [
      {
        id: "shovel-tool",
        name: "Shovel",
        category: "equipment",
        keywords: [],
        notes: "COVER on Open terrain until move. Can use as 2-Handed Melee."
      }
    ],
            upgrades: []
          },
          {
            name: "Lion of Jabir",
            class: "Lion of Jabir",
            type: "trooper",
            cost: 60,
            costCurrency: "ducats",
            stats: {
              movement: 8,
              melee: 1,
              ranged: 0,
              baseArmour: 0,
              finalArmour: 0
            },
            keywords: ["SULTANATE", "ARTIFICIAL"],
            abilities: [
              "Agile: +1 DICE Climb/Jump/Diving Charge/Dash.",
              "Artificial Life: -1 INJURY DICE.",
              "Teeth and Claws: Can melee unarmed.",
              "Pin: Down enemies ≤40mm can't stand if within 1\".",
              "+5 ducats for FEAR."
            ],
            skills: [],
            itemTemplateIds: [],
            upgrades: []
          },
          {
            name: "Janissary",
            class: "Janissary",
            type: "trooper",
            cost: 55,
            costCurrency: "ducats",
            stats: {
              movement: 6,
              melee: 1,
              ranged: 1,
              baseArmour: 0,
              finalArmour: 0
            },
            keywords: ["SULTANATE", "STRONG"],
            abilities: ["Counter-Charge: +1 DICE Melee after Charge."],
            skills: [],
            itemTemplateIds: [],
            upgrades: []
          },
          {
            name: "Brazen Bull",
            class: "Brazen Bull",
            type: "elite",
            cost: 100,
            costCurrency: "ducats",
            stats: {
              movement: 6,
              melee: 2,
              ranged: 0,
              baseArmour: 0,
              finalArmour: 0
            },
            keywords: ["SULTANATE", "ARTIFICIAL", "FEAR", "STRONG", "TOUGH"],
            abilities: [
              "Artificial Life: -1 INJURY DICE.",
              "Trample ACTION: Melee vs Down with IGNORE ARMOUR."
            ],
            skills: [],
            itemTemplateIds: [],
            upgrades: []
          }
        ],
        items: IRON_SULTANATE_STANDARD_ITEMS
      },
      "FIDA'I OF ALAMUT": {
        name: "FIDA'I OF ALAMUT",
        description: "The secretive assassins of the Iron Sultanate, masters of temporal manipulation and infiltration",
        models: IRON_SULTANATE_ALAMUT_MODELS,
        items: IRON_SULTANATE_ALAMUT_ITEMS
      },
      "House of Wisdom": {
        name: "House of Wisdom",
        description: "The scholarly and alchemical masters of the Iron Sultanate, creators of artificial life and elemental mastery",
        models: IRON_SULTANATE_WISDOM_MODELS,
        items: IRON_SULTANATE_STANDARD_ITEMS
      },
      "Iron Wall": {
        name: "Iron Wall",
        description: "The disciplined and heavily armed defensive masters of the Iron Sultanate, masters of siege warfare and unbreakable formations",
        models: IRON_SULTANATE_IRON_WALL_MODELS,
        items: IRON_SULTANATE_IRON_WALL_ITEMS
      }
    }
  },

  "Heretic Legions": {
    name: "Heretic Legions",
    description: "The corrupted legions of the Heretic forces",
    variants: {
      "Standard": {
        name: "Standard Heretic Legions",
        description: "The classic Heretic Legions army list",
        models: [] as PremadeModelTemplate[], // TODO: Add Heretic Legions models
        items: [] // TODO: Add Heretic Legions items
      }
    }
  },

  "New Antioch": {
    name: "New Antioch",
    description: "The noble houses of New Antioch",
    variants: {
      "Standard": {
        name: "Standard New Antioch",
        description: "The classic New Antioch army list",
        models: NEW_ANTIOCH_MODELS,
        items: NEW_ANTIOCH_ITEMS
      },
      "Papal States Intervention Force": {
        name: "Papal States Intervention Force",
        description: "The holy warriors of the Papal States, blessed by the Supreme Pontiff and protected by Swiss Guards",
        models: NEW_ANTIOCH_PAPAL_MODELS,
        items: NEW_ANTIOCH_PAPAL_ITEMS
      },
      "Éire Rangers": {
        name: "Éire Rangers",
        description: "The fierce warriors of Éire, masters of guerrilla warfare and berserker fury",
        models: NEW_ANTIOCH_EIRE_MODELS,
        items: NEW_ANTIOCH_EIRE_ITEMS
      },
      "Kingdom of Alba Assault Detachment": {
        name: "Kingdom of Alba Assault Detachment",
        description: "The highland warriors of Alba, masters of the charge with unbreakable morale and heavy weapons",
        models: NEW_ANTIOCH_ALBA_MODELS,
        items: NEW_ANTIOCH_ALBA_ITEMS
      },
      "Stosstruppen of the Free State of Prussia": {
        name: "Stosstruppen of the Free State of Prussia",
        description: "The elite Prussian stormtroopers, masters of infiltration and grenade warfare",
        models: NEW_ANTIOCH_PRUSSIA_MODELS,
        items: NEW_ANTIOCH_PRUSSIA_ITEMS
      },
      "Expeditionary Forces of Abyssinia": {
        name: "Expeditionary Forces of Abyssinia",
        description: "The ancient warriors of Abyssinia, masters of marksmanship and blessed by their holy relics",
        models: NEW_ANTIOCH_ABYSSINIA_MODELS,
        items: NEW_ANTIOCH_ABYSSINIA_ITEMS
      }
    }
  },

  "Black Grail": {
    name: "Black Grail",
    description: "The plague-ridden followers of the Black Grail",
    variants: {
      "Standard": {
        name: "Standard Black Grail",
        description: "The classic Black Grail army list",
        models: [] as PremadeModelTemplate[], // TODO: Add Black Grail models
        items: [] // TODO: Add Black Grail items
      }
    }
  },

  "The Court": {
    name: "The Court",
    description: "The aristocratic and demonic forces of The Court",
    variants: {
      "Standard": {
        name: "Standard Court",
        description: "The classic Court army list",
        models: [] as PremadeModelTemplate[], // TODO: Add Court models
        items: [] // TODO: Add Court items
      }
    }
  },

  "Mercenaries": {
    name: "Mercenaries",
    description: "A collection of diverse mercenaries, each with unique skills and abilities",
    variants: {
      "Standard": {
        name: "Standard Mercenaries",
        description: "A diverse group of mercenaries available for hire",
        models: MERCENARY_MODELS,
        items: MERCENARY_ITEMS
      }
    }
  }
};
